/**
 * Coach — Business Coach dashboard. Replaces the monthly CFO PDF Kyle
 * Stroshein used to deliver. Pulls live data from QuickBooks for any month,
 * lays out Kyle's Month + YTD variance heatmap, the balance-sheet snapshot,
 * marketing vendor breakdown, and an editable budget.
 *
 * Data flow:
 *   coach_snapshots stores the raw QB JSON (P&L, BS) — the UI computes the
 *   variance table by mapping accounts to buckets via coach_bucket_map and
 *   comparing against coach_budgets. Remapping accounts never requires a
 *   re-pull from QB.
 */
import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  Legend, Cell, ComposedChart, Line, PieChart, Pie, Area, ReferenceLine,
} from "recharts";
import { apiFetch } from "../lib/apiFetch";

const API = "/api/coach";

// Kyle's report buckets, in the order they appear in his variance table
const DIRECT_COST_BUCKETS = ["labor", "material", "subcontractors", "other_directs"];
const OVERHEAD_BUCKETS    = ["owner_wages", "admin_wages", "marketing", "vehicle", "other_indirects"];
const ALL_BUCKETS         = ["revenue", ...DIRECT_COST_BUCKETS, ...OVERHEAD_BUCKETS, "non_opex", "ignore"];

const BUCKET_LABEL = {
  revenue:         "Revenue",
  labor:           "Labor",
  material:        "Material",
  subcontractors:  "Subcontractors",
  other_directs:   "Other Directs",
  owner_wages:     "Owner Wages",
  admin_wages:     "Admin Wages (staff)",
  marketing:       "Marketing",
  vehicle:         "Vehicle",
  other_indirects: "Other Indirects",
  non_opex:        "Non-OPEX",
  ignore:          "Ignore",
};

const FY_START_MONTH = 6; // June

// ── Formatting ────────────────────────────────────────────────────────────
const fmtMoney = (n) => {
  const v = Number(n || 0);
  if (!isFinite(v)) return "—";
  return v.toLocaleString("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 });
};
const fmtPct = (n, digits = 0) => {
  const v = Number(n);
  if (!isFinite(v)) return "—";
  return `${(v * 100).toFixed(digits)}%`;
};
const fmtChange = (n) => {
  if (!isFinite(n)) return "—";
  const s = (n >= 0 ? "+" : "") + (n * 100).toFixed(0) + "%";
  return s;
};

// Color-codes a variance % the way Kyle did: positive variance on revenue/GP/net
// is green, negative is red. Cost buckets flip — over-budget is red.
function varianceColor(varPct, isCost) {
  if (!isFinite(varPct)) return "#9ca3af";
  if (Math.abs(varPct) < 0.05) return "#9ca3af";
  const goodIfPositive = !isCost;
  const isGood = goodIfPositive ? varPct > 0 : varPct < 0;
  return isGood ? "#22c55e" : "#ef4444";
}

// ── Period helpers ────────────────────────────────────────────────────────
function periodLabel(period) {
  if (!period) return "—";
  const [y, m] = period.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("en-CA", {
    month: "long", year: "numeric", timeZone: "UTC",
  });
}
function fiscalYearForPeriod(period) {
  const [y, m] = period.split("-").map(Number);
  return m >= FY_START_MONTH ? y + 1 : y;     // fiscal year *ending* May (e.g. 2026)
}
function currentPeriod() {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}
function hoursSince(iso) {
  if (!iso) return Infinity;
  return (Date.now() - new Date(iso).getTime()) / 36e5;
}
// Month numbers (calendar, "01".."12") that fall in fiscal-year months from
// FY start through the given period — used to sum YTD budget weights.
function ytdMonths(period) {
  const [y, m] = period.split("-").map(Number);
  const out = [];
  // FY runs from FY_START_MONTH of (fy-1) through (FY_START_MONTH-1) of fy
  const fy = fiscalYearForPeriod(period);
  const fyStartCalYear = fy - 1;
  let cy = fyStartCalYear;
  let cm = FY_START_MONTH;
  while (true) {
    out.push({ year: cy, month: cm, key: String(cm).padStart(2, "0") });
    if (cy === y && cm === m) break;
    cm++;
    if (cm > 12) { cm = 1; cy++; }
    if (out.length > 24) break; // safety
  }
  return out;
}

// ── Fiscal calendar (FY starts June) ───────────────────────────────────────
// Fiscal year is labelled by the calendar year it *ends* (May). FY2026 runs
// June 2025 → May 2026. Quarters: Q1 Jun–Aug, Q2 Sep–Nov, Q3 Dec–Feb, Q4 Mar–May.
// Returns the 12 'YYYY-MM' period keys for a fiscal year, in chronological order.
function fyMonths(fy) {
  const out = [];
  let cy = fy - 1, cm = FY_START_MONTH;
  for (let i = 0; i < 12; i++) {
    out.push(`${cy}-${String(cm).padStart(2, "0")}`);
    cm++; if (cm > 12) { cm = 1; cy++; }
  }
  return out;
}
// Which fiscal quarter (1..4) a 'YYYY-MM' period falls in.
function quarterOfPeriod(period) {
  const m = Number(period.split("-")[1]);
  // Months from FY start (June=0 … May=11)
  const idx = (m - FY_START_MONTH + 12) % 12;
  return Math.floor(idx / 3) + 1;
}
// The (up to 3) 'YYYY-MM' keys in fiscal quarter q of fiscal year fy.
function fiscalQuarterMonths(fy, q) {
  return fyMonths(fy).slice((q - 1) * 3, q * 3);
}
// The period keys covered by the current view.
function monthsInView(viewMode, anchorPeriod, quarter) {
  if (!anchorPeriod) return [];
  const fy = fiscalYearForPeriod(anchorPeriod);
  if (viewMode === "fy") return fyMonths(fy);
  if (viewMode === "quarter") return fiscalQuarterMonths(fy, quarter || quarterOfPeriod(anchorPeriod));
  return [anchorPeriod];
}
// Human label for the selected range.
function rangeLabel(viewMode, anchorPeriod, quarter) {
  if (!anchorPeriod) return "—";
  const fy = fiscalYearForPeriod(anchorPeriod);
  if (viewMode === "fy") return `FY${fy} (Jun ${fy - 1} – May ${fy})`;
  if (viewMode === "quarter") {
    const q = quarter || quarterOfPeriod(anchorPeriod);
    const ms = fiscalQuarterMonths(fy, q);
    return `FY${fy} Q${q} (${periodLabel(ms[0]).split(" ")[0]}–${periodLabel(ms[2]).split(" ")[0]} ${ms[2].slice(0, 4)})`;
  }
  return periodLabel(anchorPeriod);
}

// Chart palette — matches the dark dashboard theme.
const CHART = {
  actual: "#a5ce3a", target: "#60a5fa", net: "#22c55e", over: "#ef4444",
  under: "#22c55e", grid: "rgba(255,255,255,0.06)", axis: "#8892a4",
};
// Donut slice colors, bucket order: directs then overhead then net.
const SLICE_COLORS = {
  labor: "#60a5fa", material: "#ef4444", subcontractors: "#a78bfa", other_directs: "#7c8aa5",
  owner_wages: "#fbbf24", admin_wages: "#f59e0b", marketing: "#ec4899", vehicle: "#14b8a6", other_indirects: "#8892a4",
  non_opex: "#475569", net_profit: "#a5ce3a",
};

// Sum a P&L bucket-totals object across several months.
function sumBuckets(list) {
  const out = {};
  for (const b of list) for (const k of Object.keys(b)) out[k] = (out[k] || 0) + (b[k] || 0);
  return out;
}
// Apply manual FY reclass corrections to a bucket-totals object (mutates).
// Each entry moves `amount` from bucket `from` (optional) to bucket `to`.
// Net-zero when `from` is set (pure reclass); a cost add when `from` is blank.
function applyReclass(buckets, list) {
  for (const a of list || []) {
    const amt = Number(a.amount) || 0;
    if (!amt || !a.to) continue;
    if (a.from) buckets[a.from] = (buckets[a.from] || 0) - amt;
    buckets[a.to] = (buckets[a.to] || 0) + amt;
  }
  return buckets;
}

// Revenue / direct / overhead / GP / net from a bucket-totals object.
function summarize(buckets) {
  const revenue  = buckets.revenue || 0;
  const direct   = DIRECT_COST_BUCKETS.reduce((s, b) => s + (buckets[b] || 0), 0);
  const overhead = OVERHEAD_BUCKETS.reduce((s, b) => s + (buckets[b] || 0), 0);
  const nonOpex  = buckets.non_opex || 0;
  const gp  = revenue - direct;
  const net = gp - overhead - nonOpex;
  return { revenue, direct, overhead, nonOpex, gp, net };
}

// ── P&L flattening / bucketing ────────────────────────────────────────────
// Walks QB ProfitAndLoss raw JSON → flat [{name, parent, amount}] of leaf rows.
function flattenPl(report) {
  const out = [];
  function walk(row, parent) {
    if (row.type === "Section") {
      const headerName = row.Header?.ColData?.[0]?.value || parent || "";
      const top = parent || headerName;
      for (const sub of row.Rows?.Row || []) walk(sub, top);
    } else if (row.type === "Data") {
      out.push({
        name: row.ColData?.[0]?.value || "",
        amount: parseFloat(row.ColData?.[1]?.value || "0"),
        parent: parent || "",
      });
    }
  }
  for (const r of report?.Rows?.Row || []) walk(r, "");
  return out;
}

// QuickBooks' OWN summary lines from a ProfitAndLoss report — the authoritative
// numbers (what shows on the QB P&L). We anchor the headline net/revenue to
// these so ElecBid always ties to QuickBooks, regardless of bucket mapping.
function extractPlSummary(report) {
  const out = { income: 0, cogs: 0, grossProfit: 0, expenses: 0, netOpIncome: 0, netIncome: 0, found: false };
  const val = (row) => {
    const cd = row?.Summary?.ColData;
    if (!cd || !cd.length) return 0;
    return parseFloat(cd[cd.length - 1]?.value || "0") || 0;
  };
  for (const r of (report?.Rows?.Row || [])) {
    switch (r.group) {
      case "Income":              out.income = val(r); out.found = true; break;
      case "COGS":                out.cogs = val(r); break;
      case "GrossProfit":         out.grossProfit = val(r); break;
      // QB's modernized Reports backend (Group 1 cutover 2026-07) splits "Other
      // Expenses" into its own group row; legacy folded it into Expenses. Sum
      // both so the opex subtotal stays whole regardless of which backend
      // served the report (responses are mixed during the ~2-week ramp).
      case "Expenses":            out.expenses += val(r); break;
      case "OtherExpenses":       out.expenses += val(r); break;
      case "NetOperatingIncome":  out.netOpIncome = val(r); break;
      // Modernized backend renamed the NetIncome group to "Profit" (same value).
      // Accept both or the QB net income reads $0 after cutover.
      case "NetIncome":
      case "Profit":              out.netIncome = val(r); out.found = true; break;
      default: break;
    }
  }
  return out;
}
function sumPlSummaries(list) {
  return (list || []).reduce((a, b) => ({
    income: a.income + b.income, cogs: a.cogs + b.cogs, grossProfit: a.grossProfit + b.grossProfit,
    expenses: a.expenses + b.expenses, netOpIncome: a.netOpIncome + b.netOpIncome,
    netIncome: a.netIncome + b.netIncome, found: a.found || b.found,
  }), { income: 0, cogs: 0, grossProfit: 0, expenses: 0, netOpIncome: 0, netIncome: 0, found: false });
}

// Sums leaf amounts grouped by Kyle bucket using coach_bucket_map.
function bucketTotals(rawPl, bucketMap, splitMap = {}) {
  const flat = flattenPl(rawPl || {});
  const totals = Object.fromEntries(ALL_BUCKETS.map(b => [b, 0]));
  for (const r of flat) {
    const split = splitMap[r.name];
    if (split && Object.keys(split).length) {
      // ElecBid-only allocation: distribute this account across buckets by
      // fraction (e.g. Colton 75% labor / 25% admin) without touching QB.
      for (const [b, frac] of Object.entries(split)) totals[b] = (totals[b] || 0) + r.amount * Number(frac);
    } else {
      const bucket = bucketMap[r.name] || "ignore";
      totals[bucket] = (totals[bucket] || 0) + r.amount;
    }
  }
  return totals;
}

// Balance sheet totals are computed server-side using QB AccountType (the
// definitive classification) and stored on the snapshot. The client used
// to fall back to a regex-based parser, but that produced wrong numbers on
// any chart-of-accounts whose section headers didn't match the regex.
// Snapshots taken before bs_totals existed are flagged for re-refresh.

// ── Variance row computation ─────────────────────────────────────────────
// For a bucket, returns the row used in the heatmap.
function computeRow(bucket, monthActual, monthRevenue, monthBudget) {
  const actualPct  = monthRevenue > 0 ? monthActual / monthRevenue : 0;
  const budgetPct  = monthBudget?.pct || 0;
  const budgetAmt  = monthBudget?.amount || 0;
  const variance   = monthActual - budgetAmt;
  const variancePct = budgetAmt > 0 ? variance / budgetAmt : (monthActual > 0 ? 1 : 0);
  return { bucket, actual: monthActual, actualPct, budgetAmt, budgetPct, variance, variancePct };
}

// ─────────────────────────────────────────────────────────────────────────
export default function Coach() {
  const navigate = useNavigate();
  const [snapshots, setSnapshots]   = useState([]);
  const [snapshot, setSnapshot]     = useState(null);
  const [period, setPeriod]         = useState("");
  const [bucketMap, setBucketMap]   = useState({});      // {accountName: bucket}
  const [bucketRows, setBucketRows] = useState([]);      // raw rows for editing
  const [budget, setBudget]         = useState(null);
  const [busy, setBusy]             = useState("");      // "" | "loading" | "refreshing" | "saving"
  const [error, setError]           = useState("");
  const [status, setStatus]         = useState("");
  const [qbConnected, setQbConnected] = useState(null);  // null = unknown
  const [showBudget, setShowBudget] = useState(false);
  const [showMapping, setShowMapping] = useState(false);
  const [audit, setAudit] = useState(null);
  const [showAudit, setShowAudit] = useState(false);
  const [viewMode, setViewMode] = useState("fy");   // "month" | "quarter" | "fy" — default to the full year
  const [quarter, setQuarter]   = useState(null);       // 1..4 (defaults to anchor's quarter)
  const [monthCache, setMonthCache] = useState({});     // { 'YYYY-MM': snapshot } for the fiscal year
  const [backfill, setBackfill] = useState(null);       // { done, total, label } | null
  const [planningMode, setPlanningMode] = useState(false);
  const [scenarios, setScenarios] = useState([]);       // saved scenarios for the FY
  const [activeScenario, setActiveScenario] = useState({ name: "", planned_revenue: "", planned_pcts: {}, addons: [], owner: {}, tax: {}, wages: {}, bonuses: {}, burden_pct: "" });
  const [aiSummary, setAiSummary] = useState(null);
  const [planBusy, setPlanBusy] = useState("");          // "" | "scenario" | "ai"
  const [employees, setEmployees] = useState([]);        // roster (with actual_hours from timesheets)
  const [rosterBusy, setRosterBusy] = useState(false);

  // ── Boot: check QB, load snapshots + budget + map ──
  useEffect(() => {
    (async () => {
      try {
        setBusy("loading");
        const [statRes, snapRes, mapRes] = await Promise.all([
          apiFetch(`${API}?action=qb-status`).then(r => r.json()),
          apiFetch(`${API}?action=list-snapshots`).then(r => r.json()),
          apiFetch(`${API}?action=get-bucket-map`).then(r => r.json()),
        ]);
        setQbConnected(!!statRes?.connected);
        const snaps = snapRes?.snapshots || [];
        setSnapshots(snaps);
        const map = {};
        for (const e of (mapRes?.entries || [])) map[e.qb_account_name] = e.bucket;
        setBucketMap(map);
        setBucketRows(mapRes?.entries || []);
        // Auto-select the most recent snapshot, or default to March 2026 if none
        const initialPeriod = snaps[0]?.period || "2026-03";
        setPeriod(initialPeriod);
      } catch (e) {
        setError(e.message);
      } finally {
        setBusy("");
      }
    })();
  }, []);

  // ── Load snapshot + budget when period changes ──
  useEffect(() => {
    if (!period) return;
    (async () => {
      try {
        setBusy("loading");
        const fy = fiscalYearForPeriod(period);
        const [snapRes, budgetRes] = await Promise.all([
          apiFetch(`${API}?action=get-snapshot&period=${period}`).then(r => r.json()),
          apiFetch(`${API}?action=get-budget&year=${fy}`).then(r => r.json()),
        ]);
        setSnapshot(snapRes?.snapshot || null);
        setBudget(budgetRes?.budget || null);
        if (snapRes?.snapshot) setMonthCache(prev => ({ ...prev, [period]: snapRes.snapshot }));
        setQuarter(quarterOfPeriod(period));
      } catch (e) {
        setError(e.message);
      } finally {
        setBusy("");
      }
    })();
  }, [period]);

  // ── Load every fiscal-year month that has data into the cache ──
  // Powers the Quarter/FY roll-ups and the revenue/net trend chart. Only
  // fetches periods that appear in the snapshots list (already pulled from QB),
  // so it never triggers a QB call — pure Supabase reads of existing snapshots.
  useEffect(() => {
    if (!period) return;
    const fy = fiscalYearForPeriod(period);
    const wanted = fyMonths(fy).filter(k => snapshots.some(s => s.period === k));
    const missing = wanted.filter(k => !monthCache[k]);
    if (!missing.length) return;
    (async () => {
      const results = await Promise.all(missing.map(k =>
        apiFetch(`${API}?action=get-snapshot&period=${k}`)
          .then(r => r.json()).then(j => [k, j?.snapshot || null]).catch(() => [k, null])
      ));
      setMonthCache(prev => {
        const next = { ...prev };
        for (const [k, s] of results) if (s) next[k] = s;
        return next;
      });
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, snapshots]);

  // ── Setup (idempotent — seeds default budget if missing) ──
  const runSetup = useCallback(async () => {
    setBusy("saving"); setError(""); setStatus("");
    try {
      const fy = period ? fiscalYearForPeriod(period) : new Date().getFullYear();
      const r = await apiFetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "setup", year: fy }),
      }).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Setup failed");
      // Reload budget
      const bres = await apiFetch(`${API}?action=get-budget&year=${fy}`).then(r => r.json());
      setBudget(bres?.budget || null);
      setStatus("Defaults loaded.");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  }, [period]);

  // ── Refresh from QB ──
  const refreshFromQb = useCallback(async (targetPeriod) => {
    if (!targetPeriod) return;
    setBusy("refreshing"); setError(""); setStatus("");
    try {
      const r = await apiFetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "refresh-snapshot", period: targetPeriod }),
      }).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Refresh failed");
      // Reload everything
      const [snapRes, snapsRes, mapRes] = await Promise.all([
        apiFetch(`${API}?action=get-snapshot&period=${targetPeriod}`).then(r => r.json()),
        apiFetch(`${API}?action=list-snapshots`).then(r => r.json()),
        apiFetch(`${API}?action=get-bucket-map`).then(r => r.json()),
      ]);
      setSnapshot(snapRes?.snapshot || null);
      setSnapshots(snapsRes?.snapshots || []);
      const map = {};
      for (const e of (mapRes?.entries || [])) map[e.qb_account_name] = e.bucket;
      setBucketMap(map);
      setBucketRows(mapRes?.entries || []);
      setStatus(`Pulled ${targetPeriod}. ${r.new_account_mappings} new account(s) auto-mapped. ${r.marketing_vendor_count} marketing vendor(s).`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  }, []);

  // ── Save budget ──
  const saveBudget = useCallback(async (next) => {
    setBusy("saving"); setError(""); setStatus("");
    try {
      const r = await apiFetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "save-budget", ...next }),
      }).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Save failed");
      setBudget(next);
      setStatus("Budget saved.");
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  }, []);

  // ── Save bucket map ──
  const saveBucketMap = useCallback(async (entries) => {
    setBusy("saving"); setError(""); setStatus("");
    try {
      const r = await apiFetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "save-bucket-map", entries }),
      }).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Save failed");
      const map = {};
      for (const e of entries) map[e.qb_account_name] = e.bucket;
      setBucketMap(prev => ({ ...prev, ...map }));
      setBucketRows(prev => prev.map(p => {
        const u = entries.find(e => e.qb_account_name === p.qb_account_name);
        return u ? { ...p, ...u, auto_assigned: false } : p;
      }));
      setStatus(`Saved ${entries.length} mapping(s).`);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  }, []);

  // ── Backfill the fiscal year from QB (sequential to avoid 429) ──
  const backfillFy = useCallback(async () => {
    if (!period || !qbConnected) return;
    const fy = fiscalYearForPeriod(period);
    const target  = fyMonths(fy).filter(k => k <= currentPeriod());
    const missing = target.filter(k => !snapshots.some(s => s.period === k));
    if (!missing.length) { setStatus("Fiscal year already complete — nothing to backfill."); return; }
    setError(""); setStatus("");
    let ok = 0;
    for (let i = 0; i < missing.length; i++) {
      setBackfill({ done: i, total: missing.length, label: periodLabel(missing[i]) });
      try {
        const r = await apiFetch(API, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "refresh-snapshot", period: missing[i] }),
        }).then(r => r.json());
        if (r.ok) ok++;
      } catch { /* keep going — partial backfill is fine */ }
    }
    setBackfill(null);
    // Reload snapshot list + mapping, drop the month cache so it re-hydrates.
    const [snapsRes, mapRes] = await Promise.all([
      apiFetch(`${API}?action=list-snapshots`).then(r => r.json()),
      apiFetch(`${API}?action=get-bucket-map`).then(r => r.json()),
    ]);
    setSnapshots(snapsRes?.snapshots || []);
    const map = {};
    for (const e of (mapRes?.entries || [])) map[e.qb_account_name] = e.bucket;
    setBucketMap(map);
    setBucketRows(mapRes?.entries || []);
    setMonthCache({});
    setStatus(`Backfill complete — ${ok}/${missing.length} month(s) pulled from QuickBooks.`);
  }, [period, qbConnected, snapshots]);

  // Per-account split allocations (ElecBid-only) keyed by QB account name.
  const splitMap = useMemo(() => {
    const m = {};
    for (const r of bucketRows) if (r.split && Object.keys(r.split).length) m[r.qb_account_name] = r.split;
    return m;
  }, [bucketRows]);

  // ── Compute variance rows for the selected period ──
  const computed = useMemo(() => {
    if (!budget) return null;
    // Months covered by the selected view (Month / Quarter / Fiscal Year).
    const months = monthsInView(viewMode, period, quarter);
    // Snapshots we actually have for those months (anchor included via cache).
    const inView = months.map(k => monthCache[k] || (k === period ? snapshot : null)).filter(Boolean);
    const effective = inView.length ? inView : (snapshot ? [snapshot] : []);
    if (!effective.length) return null;
    // The "range" (left column) is the sum across in-view months; the YTD
    // (right column) comes from the latest in-view month's raw_pl_ytd, which
    // QB already returns as fiscal-year-to-date.
    const latest = effective.reduce((a, b) => (a.period > b.period ? a : b));
    const presentMonths = effective.map(s => s.period).sort();
    const expectedMonths = months.filter(k => k <= currentPeriod());
    const missingMonths  = expectedMonths.filter(k => !presentMonths.includes(k));

    const isFY = viewMode === "fy";
    const ytdBuckets   = bucketTotals(latest.raw_pl_ytd, bucketMap, splitMap);
    // FY view uses QuickBooks' OWN year-to-date report (a single current pull),
    // not a sum of per-month snapshots that may be individually stale. Quarter
    // and Month still aggregate the relevant monthly snapshots.
    const monthBuckets = isFY
      ? bucketTotals(latest.raw_pl_ytd, bucketMap, splitMap)
      : sumBuckets(effective.map(s => bucketTotals(s.raw_pl, bucketMap, splitMap)));
    // FY reclass corrections apply to the full-year picture only.
    if (budget.reclass?.length && isFY) {
      applyReclass(monthBuckets, budget.reclass);
      applyReclass(ytdBuckets, budget.reclass);
    }
    // Raw QB bucketing (no splits, no reclass) for the QB-vs-ElecBid overlay.
    const rawBuckets = isFY
      ? bucketTotals(latest.raw_pl_ytd, bucketMap)
      : sumBuckets(effective.map(s => bucketTotals(s.raw_pl, bucketMap)));
    // QuickBooks' own summary lines — authoritative headline net/revenue.
    const qbYtdSum = extractPlSummary(latest.raw_pl_ytd);
    const qbRange  = isFY ? qbYtdSum : sumPlSummaries(effective.map(s => extractPlSummary(s.raw_pl)));

    const monthRevenue = monthBuckets.revenue;
    const ytdRevenue   = ytdBuckets.revenue;

    const rangeWeight = months.reduce((s, k) => s + (budget.monthly_weights?.[k.slice(5, 7)] || 0), 0);
    const monthBudgetRevenue = budget.annual_revenue_target * rangeWeight;
    const ytdWeight = ytdMonths(latest.period).reduce((sum, m) => sum + (budget.monthly_weights?.[m.key] || 0), 0);
    const ytdBudgetRevenue = budget.annual_revenue_target * ytdWeight;

    function buildRow(bucket, isCost, monthActual, ytdActual) {
      const pct = budget.line_pcts?.[bucket] || 0;
      const monthBudgetAmt = monthBudgetRevenue * pct;
      const ytdBudgetAmt   = ytdBudgetRevenue * pct;
      return {
        bucket,
        isCost,
        month: computeRow(bucket, monthActual, monthRevenue, { pct, amount: monthBudgetAmt }),
        ytd:   computeRow(bucket, ytdActual, ytdRevenue, { pct, amount: ytdBudgetAmt }),
      };
    }

    const directRows   = DIRECT_COST_BUCKETS.map(b => buildRow(b, true, monthBuckets[b], ytdBuckets[b]));
    const overheadRows = OVERHEAD_BUCKETS.map(b => buildRow(b, true, monthBuckets[b], ytdBuckets[b]));
    const monthDirect = directRows.reduce((s, r) => s + r.month.actual, 0);
    const ytdDirect   = directRows.reduce((s, r) => s + r.ytd.actual, 0);
    const monthOverhead = overheadRows.reduce((s, r) => s + r.month.actual, 0);
    const ytdOverhead   = overheadRows.reduce((s, r) => s + r.ytd.actual, 0);
    const monthGP = monthRevenue - monthDirect;
    const ytdGP   = ytdRevenue - ytdDirect;
    const monthNet = monthGP - monthOverhead - monthBuckets.non_opex;
    const ytdNet   = ytdGP - ytdOverhead - ytdBuckets.non_opex;

    const monthDirectBudget   = monthBudgetRevenue * (DIRECT_COST_BUCKETS.reduce((s, b) => s + (budget.line_pcts?.[b] || 0), 0));
    const ytdDirectBudget     = ytdBudgetRevenue   * (DIRECT_COST_BUCKETS.reduce((s, b) => s + (budget.line_pcts?.[b] || 0), 0));
    const monthOverheadBudget = monthBudgetRevenue * (OVERHEAD_BUCKETS.reduce((s, b) => s + (budget.line_pcts?.[b] || 0), 0));
    const ytdOverheadBudget   = ytdBudgetRevenue   * (OVERHEAD_BUCKETS.reduce((s, b) => s + (budget.line_pcts?.[b] || 0), 0));
    const monthGpBudget   = monthBudgetRevenue - monthDirectBudget;
    const ytdGpBudget     = ytdBudgetRevenue   - ytdDirectBudget;
    const monthNetBudget  = monthBudgetRevenue * (budget.net_profit_target_pct || 0.09);
    const ytdNetBudget    = ytdBudgetRevenue   * (budget.net_profit_target_pct || 0.09);

    // Balance sheet — server-computed totals only. If the snapshot was taken
    // before bs_totals was added, mark stale and zero everything out so the
    // UI can prompt a re-refresh instead of showing wrong numbers.
    const stale   = !latest.bs_totals;
    const empty   = { cash: 0, ar: 0, ap: 0, credit_cards: 0, loc: 0, inventory: 0,
                      total_assets: 0, total_liabilities: 0, total_equity: 0,
                      current_assets: 0, current_liabilities: 0, unclassified: [] };
    const bs      = latest.bs_totals       || empty;
    const bsPrior = latest.prior_bs_totals || empty;
    const jobberAr = latest.jobber_metrics?.ar || null;
    // AR for all ratios prefers Jobber (operationally accurate — reflects
    // what customers actually owe today, including invoices that haven't
    // been posted to QB yet). Falls back to QB BS AR when no Jobber
    // snapshot exists. Prior-period AR uses QB BS AR because we don't
    // track Jobber AR historically per snapshot — flagged in the UI.
    const arNow      = jobberAr?.outstanding ?? bs.ar;
    const arSource   = jobberAr ? "Jobber" : "QB BS";
    const arPrior    = bsPrior.ar;     // historical Jobber AR not tracked
    const quickAssets = bs.cash + arNow;
    const quickLiab   = bs.ap + bs.credit_cards + bs.loc;
    const quickRatio  = quickLiab > 0 ? quickAssets / quickLiab : null;
    const quickAssetsP = bsPrior.cash + arPrior;
    const quickLiabP   = bsPrior.ap + bsPrior.credit_cards + bsPrior.loc;
    const quickRatioPrior = quickLiabP > 0 ? quickAssetsP / quickLiabP : null;
    const debtToEquity = bs.total_equity > 0 ? bs.total_liabilities / bs.total_equity : null;
    const returnOnAssets = bs.total_assets > 0 ? ytdNet / bs.total_assets : null;
    const avgAR = ((arNow + arPrior) / 2) || 0;
    const arTurnover = avgAR > 0 ? ytdRevenue / avgAR : null;
    const monthlyOverhead = (monthOverhead / Math.max(1, effective.length)) || (ytdOverhead / Math.max(1, ytdMonths(latest.period).length));
    const cashRunwayMonths = monthlyOverhead > 0 ? (bs.cash + arNow - bs.ap - bs.credit_cards - bs.loc) / monthlyOverhead : null;

    // ── Chart-ready derived data (all from the live range above) ──
    const lp  = budget.line_pcts || {};
    const pct = (v) => (monthRevenue > 0 ? (v / monthRevenue) * 100 : 0);
    const benchmarkData = [...DIRECT_COST_BUCKETS, ...OVERHEAD_BUCKETS].map(b => {
      const actual = +pct(monthBuckets[b] || 0).toFixed(1);
      const qb = +pct(rawBuckets[b] || 0).toFixed(1);
      return {
        key: b, label: BUCKET_LABEL[b],
        actual, qb, target: +((lp[b] || 0) * 100).toFixed(1),
        qbBase:  +Math.min(qb, actual).toFixed(1),   // green: present in QB too
        added:   +Math.max(0, actual - qb).toFixed(1),  // purple: reallocated IN by ElecBid
        removed: +Math.max(0, qb - actual).toFixed(1),  // grey: moved OUT vs QB
        isCost: true, amount: monthBuckets[b] || 0,
      };
    });
    const netMarginPct = monthRevenue > 0 ? (monthNet / monthRevenue) * 100 : 0;
    benchmarkData.push({
      key: "net_profit", label: "Net Profit",
      actual: +netMarginPct.toFixed(1), qb: +netMarginPct.toFixed(1),
      target: +((budget.net_profit_target_pct || 0.09) * 100).toFixed(1),
      qbBase: +netMarginPct.toFixed(1), added: 0, removed: 0,
      isCost: false, amount: monthNet,
    });

    const compositionData = [...DIRECT_COST_BUCKETS, ...OVERHEAD_BUCKETS]
      .map(b => ({ key: b, name: BUCKET_LABEL[b], value: Math.max(0, monthBuckets[b] || 0) }))
      .filter(d => d.value > 0);
    if (monthBuckets.non_opex > 0) compositionData.push({ key: "non_opex", name: "Non-OPEX", value: monthBuckets.non_opex });
    if (monthNet > 0) compositionData.push({ key: "net_profit", name: "Net Profit", value: monthNet });

    const salary = {
      adminActual: monthBuckets.admin_wages || 0, adminActualPct: pct(monthBuckets.admin_wages || 0),
      adminTargetPct: (lp.admin_wages || 0) * 100, adminBudget: monthBudgetRevenue * (lp.admin_wages || 0),
      laborActual: monthBuckets.labor || 0, laborActualPct: pct(monthBuckets.labor || 0),
      laborTargetPct: (lp.labor || 0) * 100, laborBudget: monthBudgetRevenue * (lp.labor || 0),
    };

    const trend = fyMonths(fiscalYearForPeriod(period)).map(k => {
      const s = monthCache[k];
      const lbl = periodLabel(k).slice(0, 3);
      if (!s) return { period: k, label: lbl, revenue: null, net: null, margin: null, hasData: false };
      const sb = summarize(bucketTotals(s.raw_pl, bucketMap, splitMap));
      return {
        period: k, label: lbl, hasData: true,
        revenue: Math.round(sb.revenue), net: Math.round(sb.net),
        margin: sb.revenue > 0 ? +((sb.net / sb.revenue) * 100).toFixed(1) : 0,
      };
    });

    return {
      monthRevenue, ytdRevenue, monthBudgetRevenue, ytdBudgetRevenue,
      directRows, overheadRows,
      monthDirect, ytdDirect, monthDirectBudget, ytdDirectBudget,
      monthOverhead, ytdOverhead, monthOverheadBudget, ytdOverheadBudget,
      monthGP, ytdGP, monthGpBudget, ytdGpBudget,
      monthNet, ytdNet, monthNetBudget, ytdNetBudget,
      monthNonOpex: monthBuckets.non_opex, ytdNonOpex: ytdBuckets.non_opex,
      bs, bsPrior, quickAssets, quickLiab, quickRatio, quickRatioPrior,
      debtToEquity, returnOnAssets, arTurnover, cashRunwayMonths,
      jobberAr, arNow, arSource, stale,
      // chart + range metadata
      benchmarkData, compositionData, salary, trend,
      months, presentMonths, missingMonths, effectiveCount: effective.length,
      // QuickBooks authoritative headline (falls back to bucket-derived)
      qbNet: qbRange.found ? qbRange.netIncome : monthNet,
      qbRev: qbRange.found ? qbRange.income : monthRevenue,
      qbYtdNet: qbYtdSum.found ? qbYtdSum.netIncome : ytdNet,
      qbYtdRev: qbYtdSum.found ? qbYtdSum.income : ytdRevenue,
      bucketNet: monthNet,
    };
  }, [snapshot, monthCache, bucketMap, splitMap, budget, period, viewMode, quarter]);

  // ── Scenario planner: annualized actual baseline from the fiscal year ──
  const baseline = useMemo(() => {
    if (!period || !budget) return null;
    const fy = fiscalYearForPeriod(period);
    const months = fyMonths(fy).map(k => monthCache[k]).filter(Boolean);
    if (!months.length) return null;
    const summed = sumBuckets(months.map(s => bucketTotals(s.raw_pl, bucketMap, splitMap)));
    const factor = 12 / months.length;               // annualize partial year
    const annual = {};
    for (const k of Object.keys(summed)) annual[k] = summed[k] * factor;
    applyReclass(annual, budget.reclass);   // FY corrections (Taylor etc.)
    const s = summarize(annual);
    // Anchor revenue + net to QuickBooks' own summary lines (authoritative),
    // annualized the same way. CM uses QB COGS as the variable base.
    const qb = sumPlSummaries(months.map(m => extractPlSummary(m.raw_pl)));
    const revenue  = (qb.found && qb.income) ? qb.income * factor : s.revenue;
    const netQb    = qb.found ? qb.netIncome * factor : s.net;
    const variable = (qb.found && qb.cogs) ? qb.cogs * factor : s.direct;
    const cm = revenue > 0 ? (revenue - variable) / revenue : 0.39;
    return { annual, ...s, revenue, net: netQb, bucketNet: s.net, cm, monthsUsed: months.length, fy };
  }, [monthCache, bucketMap, splitMap, budget, period]);

  const plan = useMemo(() => {
    if (!baseline || !activeScenario) return null;
    const sc = activeScenario;
    const rev = sc.planned_revenue && Number(sc.planned_revenue) > 0 ? Number(sc.planned_revenue) : baseline.revenue;
    const pcts = sc.planned_pcts || {};
    const addonByBucket = {};
    for (const a of (sc.addons || [])) {
      const amt = (Number(a.monthly) || 0) * 12 * (a.sign === "-" ? -1 : 1);
      addonByBucket[a.bucket] = (addonByBucket[a.bucket] || 0) + amt;
    }
    const plannedBucket = {};
    for (const b of [...DIRECT_COST_BUCKETS, ...OVERHEAD_BUCKETS]) {
      const actualPct = baseline.revenue > 0 ? (baseline.annual[b] || 0) / baseline.revenue : 0;
      const hasOverride = pcts[b] != null && pcts[b] !== "";
      let base;
      if (VARIABLE_BUCKETS.includes(b)) base = rev * (hasOverride ? Number(pcts[b]) / 100 : actualPct);
      else base = hasOverride ? rev * Number(pcts[b]) / 100 : (baseline.annual[b] || 0);
      plannedBucket[b] = base + (addonByBucket[b] || 0);
    }
    // Owner payroll: planned (Brownings + Colton) vs what's currently in the
    // books. The difference lands in Admin Wages — where owner pay is coded —
    // so bumping payroll flows into net automatically. Adding a separate admin
    // hire is a normal Admin-Wages adjustment, tracked independently.
    // Wages come from the employee roster: each person's (planned − standard)
    // rate × hours (or ×12 for salary) routes to their bucket. Standard rate =
    // no delta, so this never double-counts the QB actuals. Colton's delta
    // splits office%/direct.
    const rw = rosterWages(employees, sc.wages, sc.bonuses, sc.burden_pct);
    plannedBucket.owner_wages = (plannedBucket.owner_wages || 0) + rw.deltas.owner_wages;
    plannedBucket.labor       = (plannedBucket.labor || 0) + rw.deltas.labor;
    plannedBucket.admin_wages = (plannedBucket.admin_wages || 0) + rw.deltas.admin_wages;
    const ownerAnnual = rw.ownerPlanAnnual;                 // planned owner comp (8–13% bar)
    const ownerDelta  = rw.ownerPlanAnnual - rw.ownerStdAnnual;
    const nonOpex = (baseline.annual.non_opex || 0) + (addonByBucket.non_opex || 0);
    const direct = DIRECT_COST_BUCKETS.reduce((s, b) => s + plannedBucket[b], 0);
    const overhead = OVERHEAD_BUCKETS.reduce((s, b) => s + plannedBucket[b], 0);
    // Reconcile bucket math to QuickBooks: the constant gap between QB net and
    // bucket-summed net (unmapped accounts) so the base ties to QB exactly.
    const reconcile = (baseline.net || 0) - (baseline.bucketNet || 0);
    const net = rev - direct - overhead - nonOpex + reconcile;
    // Dividends are paid from after-tax profit — a distribution, NOT a P&L
    // expense. They don't change net/margin; they reduce retained surplus.
    const dividends = (Number(sc.owner?.brownings_dividend) || 0) + (Number(sc.owner?.colton_dividend) || 0);
    const rows = [...DIRECT_COST_BUCKETS, ...OVERHEAD_BUCKETS].map(b => ({
      key: b, label: BUCKET_LABEL[b],
      actual:  +(baseline.revenue > 0 ? (baseline.annual[b] || 0) / baseline.revenue * 100 : 0).toFixed(1),
      planned: +(rev > 0 ? plannedBucket[b] / rev * 100 : 0).toFixed(1),
      target:  +((budget.line_pcts?.[b] || 0) * 100).toFixed(1),
    }));
    return {
      rev, plannedBucket, direct, overhead, nonOpex, net, margin: rev > 0 ? net / rev : 0,
      ownerAnnual, ownerPct: rev > 0 ? ownerAnnual / rev : 0, ownerDelta,
      adminPlanned: plannedBucket.admin_wages, ownerWagesPlanned: plannedBucket.owner_wages, rows,
      roster: rw, payrollStd: rw.totalStdAnnual, payrollPlan: rw.totalPlanAnnual,
      dividends, retained: net - dividends,
      extractionPct: rev > 0 ? (ownerAnnual + dividends) / rev : 0,
      actualNet: baseline.net, actualMargin: baseline.revenue > 0 ? baseline.net / baseline.revenue : 0,
    };
  }, [baseline, activeScenario, budget, employees]);

  // Load saved scenarios for the fiscal year
  useEffect(() => {
    if (!period) return;
    const fy = fiscalYearForPeriod(period);
    apiFetch(`${API}?action=list-scenarios&year=${fy}`).then(r => r.json())
      .then(j => setScenarios(j?.scenarios || [])).catch(() => {});
  }, [period]);

  // ── Employee roster: load (and seed on first use) for the fiscal year ──
  useEffect(() => {
    if (!planningMode || !period) return;
    const fy = fiscalYearForPeriod(period);
    (async () => {
      try {
        let r = await apiFetch(`${API}?action=get-employees&year=${fy}`).then(r => r.json());
        if (r.ok && (!r.employees || !r.employees.length)) {
          await apiFetch(API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "seed-employees" }) });
          r = await apiFetch(`${API}?action=get-employees&year=${fy}`).then(r => r.json());
        }
        setEmployees(r.employees || []);
      } catch { /* roster optional */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planningMode, period]);

  const updateEmployee = useCallback((name, patch) => {
    setEmployees(list => list.map(e => (e.employee_name === name ? { ...e, ...patch } : e)));
  }, []);
  const saveRoster = useCallback(async () => {
    setRosterBusy(true); setError("");
    try {
      const r = await apiFetch(API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "save-employees", employees }) }).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Save failed");
      setStatus("Standard rates saved.");
    } catch (e) { setError(e.message); } finally { setRosterBusy(false); }
  }, [employees]);
  const setWage = useCallback((name, val) => {
    setActiveScenario(s => ({ ...s, wages: { ...(s.wages || {}), [name]: val } }));
  }, []);
  const setBonus = useCallback((name, val) => {
    setActiveScenario(s => ({ ...s, bonuses: { ...(s.bonuses || {}), [name]: val } }));
  }, []);
  const setBurden = useCallback((val) => {
    setActiveScenario(s => ({ ...s, burden_pct: val }));
  }, []);

  const saveScenario = useCallback(async () => {
    if (!activeScenario.name) return;
    setPlanBusy("scenario"); setError("");
    try {
      const fy = fiscalYearForPeriod(period);
      const body = { action: "save-scenario", year: fy, ...activeScenario };
      const r = await apiFetch(API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "Save failed");
      if (r.scenario?.id) setActiveScenario(s => ({ ...s, id: r.scenario.id }));
      const list = await apiFetch(`${API}?action=list-scenarios&year=${fy}`).then(r => r.json());
      setScenarios(list?.scenarios || []);
      setStatus(`Scenario "${activeScenario.name}" saved.`);
    } catch (e) { setError(e.message); } finally { setPlanBusy(""); }
  }, [activeScenario, period]);

  const loadScenario = useCallback((id) => {
    if (!id) return;
    const s = scenarios.find(x => x.id === id);
    if (!s) return;
    setActiveScenario({
      id: s.id, name: s.name,
      planned_revenue: s.planned_revenue ?? "",
      planned_pcts: s.planned_pcts || {}, addons: s.addons || [],
      owner: s.owner || {}, tax: s.tax || {}, wages: s.wages || {},
      bonuses: s.bonuses || {}, burden_pct: s.burden_pct ?? "",
    });
    setAiSummary(null);
  }, [scenarios]);

  const deleteScenario = useCallback(async () => {
    if (!activeScenario.id) return;
    setPlanBusy("scenario"); setError("");
    try {
      await apiFetch(API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "delete-scenario", id: activeScenario.id }) });
      const fy = fiscalYearForPeriod(period);
      const list = await apiFetch(`${API}?action=list-scenarios&year=${fy}`).then(r => r.json());
      setScenarios(list?.scenarios || []);
      setActiveScenario({ name: "", planned_revenue: "", planned_pcts: {}, addons: [], owner: {}, tax: {}, wages: {}, bonuses: {}, burden_pct: "" });
      setStatus("Scenario deleted.");
    } catch (e) { setError(e.message); } finally { setPlanBusy(""); }
  }, [activeScenario, period]);

  const runAi = useCallback(async () => {
    if (!baseline || !plan) return;
    setPlanBusy("ai"); setError(""); setAiSummary(null);
    try {
      const payload = {
        scenario_name: activeScenario.name || "Untitled",
        actual: { revenue: Math.round(baseline.revenue), net: Math.round(baseline.net), margin_pct: +(plan.actualMargin * 100).toFixed(1), contribution_margin_pct: +(baseline.cm * 100).toFixed(1) },
        planned: { revenue: Math.round(plan.rev), net: Math.round(plan.net), margin_pct: +(plan.margin * 100).toFixed(1), owner_comp_pct: +(plan.ownerPct * 100).toFixed(1), dividends_taken: Math.round(plan.dividends), retained_surplus: Math.round(plan.retained) },
        adjustments: (activeScenario.addons || []).map(a => ({ name: a.name, monthly: (a.sign === "-" ? -1 : 1) * (Number(a.monthly) || 0), bucket: BUCKET_LABEL[a.bucket] || a.bucket })),
        line_pct_overrides: activeScenario.planned_pcts || {},
        per_line_actual_vs_planned_pct: plan.rows,
      };
      const r = await apiFetch(API, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "scenario-summary", payload }) }).then(r => r.json());
      if (!r.ok) throw new Error(r.error || "AI summary failed");
      setAiSummary(r.summary);
    } catch (e) { setError(e.message); } finally { setPlanBusy(""); }
  }, [baseline, plan, activeScenario]);

  // ── Generate the partner report (FY actuals + active scenario) ──
  const openReport = useCallback(() => {
    const w = window.open("", "_blank");
    if (!w) { setError("Pop-up blocked — allow pop-ups in your browser to generate the report."); return; }
    let html;
    if (baseline) {
      html = buildPartnerReport(baseline, plan, activeScenario, budget, {
        fyLabel: `FY${baseline.fy} (Jun ${baseline.fy - 1} – May ${baseline.fy})`,
        generatedAt: new Date().toLocaleString("en-CA"),
      });
    } else if (computed) {
      html = buildReportHtml(computed, {
        label: rangeLabel(viewMode, period, quarter),
        viewLabel: viewMode === "fy" ? "Fiscal Year" : viewMode === "quarter" ? "Quarter" : "Month",
        netTargetPct: (budget?.net_profit_target_pct || 0.12) * 100,
        generatedAt: new Date().toLocaleString("en-CA"),
      });
    } else {
      w.close(); setError("No data yet — Backfill the fiscal year first."); return;
    }
    w.document.write(html);
    w.document.close();
  }, [baseline, plan, activeScenario, computed, viewMode, period, quarter, budget]);

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div style={S.page}>
      <div style={S.header}>
        <div>
          <div style={S.title}>Business Coach</div>
          <div style={S.subtitle}>Monthly variance, balance sheet, and budget — live from QuickBooks.</div>
        </div>
        <div style={S.headerActions}>
          {qbConnected === false && (
            <a href="/api/quickbooks-auth?action=connect" style={S.btnDanger}>Connect QuickBooks</a>
          )}
          {qbConnected && !budget && (
            <button onClick={runSetup} disabled={!!busy} style={S.btnPrimary}>
              {busy === "saving" ? "Setting up…" : "Run Setup"}
            </button>
          )}
        </div>
      </div>

      {error && <div style={S.errorBanner}>{error}<button style={S.bannerClose} onClick={() => setError("")}>×</button></div>}
      {status && <div style={S.statusBanner}>{status}<button style={S.bannerClose} onClick={() => setStatus("")}>×</button></div>}
      {backfill && (
        <div style={S.statusBanner}>
          <span>Backfilling fiscal year — {backfill.done}/{backfill.total} · pulling {backfill.label} from QuickBooks…</span>
        </div>
      )}

      {/* Business Meetings entry — biweekly Thursday cadence, separate page */}
      <button onClick={() => navigate("/coach/meetings")} style={S.meetingsCard}>
        <div style={S.meetingsCardIcon} aria-hidden>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect width="18" height="18" x="3" y="4" rx="2"/>
            <line x1="16" y1="2" x2="16" y2="6"/>
            <line x1="8" y1="2" x2="8" y2="6"/>
            <line x1="3" y1="10" x2="21" y2="10"/>
            <path d="M8 14h.01"/><path d="M12 14h.01"/><path d="M16 14h.01"/>
          </svg>
        </div>
        <div style={{ flex: 1, textAlign: "left" }}>
          <div style={S.meetingsCardTitle}>Business Meetings</div>
          <div style={S.meetingsCardDesc}>Biweekly planning, goals tracker, action items, KPI review.</div>
        </div>
        <div style={S.meetingsCardArrow}>›</div>
      </button>

      {/* Day Audit entry — passive time tracking + Jai coaching summary */}
      <button onClick={() => navigate("/coach/day-audit")} style={S.meetingsCard}>
        <div style={S.meetingsCardIcon} aria-hidden>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9"/>
            <polyline points="12 7 12 12 15 14"/>
          </svg>
        </div>
        <div style={{ flex: 1, textAlign: "left" }}>
          <div style={S.meetingsCardTitle}>Day Audit</div>
          <div style={S.meetingsCardDesc}>Where did your 8h actually go? Passive tracking + Jai's kill/delegate/keep coaching.</div>
        </div>
        <div style={S.meetingsCardArrow}>›</div>
      </button>

      {/* R&D / SR&ED report — turns tracked R&D into a CRA-ready evidence trail */}
      <button onClick={() => navigate("/coach/rnd")} style={S.meetingsCard}>
        <div style={S.meetingsCardIcon} aria-hidden>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 3h6"/><path d="M10 3v6.5L5.5 18a2 2 0 0 0 1.8 3h9.4a2 2 0 0 0 1.8-3L14 9.5V3"/><path d="M7.5 14h9"/>
          </svg>
        </div>
        <div style={{ flex: 1, textAlign: "left" }}>
          <div style={S.meetingsCardTitle}>R&amp;D / SR&amp;ED Report</div>
          <div style={S.meetingsCardDesc}>Capture R&amp;D time, proof, and a CRA-ready write-up for next year's SR&amp;ED grant.</div>
        </div>
        <div style={S.meetingsCardArrow}>›</div>
      </button>

      {/* Owner KPIs entry — Dylan & Colton input-vs-output performance scorecard */}
      <button onClick={() => navigate("/coach/owner-kpis")} style={S.meetingsCard}>
        <div style={S.meetingsCardIcon} aria-hidden>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
          </svg>
        </div>
        <div style={{ flex: 1, textAlign: "left" }}>
          <div style={S.meetingsCardTitle}>Owner KPIs</div>
          <div style={S.meetingsCardDesc}>Dylan &amp; Colton — hours, revenue on jobs worked, quotes, conversion, leads &amp; revenue by category, by week/month/year.</div>
        </div>
        <div style={S.meetingsCardArrow}>›</div>
      </button>

      {/* Selling Rate entry — what an hour has to sell for, and the rate new quotes open at */}
      <button onClick={() => navigate("/coach/sell-rate")} style={S.meetingsCard}>
        <div style={S.meetingsCardIcon} aria-hidden>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="2" x2="12" y2="22"/><path d="M17 6H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
          </svg>
        </div>
        <div style={{ flex: 1, textAlign: "left" }}>
          <div style={S.meetingsCardTitle}>Selling Rate</div>
          <div style={S.meetingsCardDesc}>What an hour has to sell for, from QuickBooks costs and timesheet hours. Sets the rate new quotes open at.</div>
        </div>
        <div style={S.meetingsCardArrow}>›</div>
      </button>

      {/* View mode — Month / Quarter / Fiscal Year (fiscal year starts June) */}
      <div style={S.viewBar}>
        <div style={S.segmented}>
          {[["month", "Month"], ["quarter", "Quarter"], ["fy", "Fiscal Year"]].map(([v, lbl]) => (
            <button key={v} onClick={() => setViewMode(v)} style={viewMode === v ? S.segActive : S.seg}>{lbl}</button>
          ))}
        </div>
        {viewMode === "quarter" && (
          <select value={quarter || 1} onChange={e => setQuarter(Number(e.target.value))} style={S.select} disabled={!!busy}>
            <option value={1}>Q1 · Jun–Aug</option>
            <option value={2}>Q2 · Sep–Nov</option>
            <option value={3}>Q3 · Dec–Feb</option>
            <option value={4}>Q4 · Mar–May</option>
          </select>
        )}
        <div style={S.rangeCaption}>{rangeLabel(viewMode, period, quarter)}</div>
        <button
          onClick={() => setPlanningMode(m => !m)}
          style={{ padding: "9px 18px", background: "#f59e0b", color: "#0d0f12", border: "none", borderRadius: 8, fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "inherit", marginLeft: "auto", boxShadow: "0 0 0 1px rgba(245,158,11,0.4)" }}
        >
          {planningMode ? "✕ Close Scenario Planner" : "📊 Scenario Planner"}
        </button>
      </div>

      <div style={S.controlsBar}>
        <label style={S.controlLabel}>{viewMode === "month" ? "Month" : "Anchor month"}</label>
        <select value={period} onChange={e => setPeriod(e.target.value)} style={S.select} disabled={!!busy}>
          {!snapshots.find(s => s.period === period) && period && (
            <option value={period}>{periodLabel(period)} (no data yet)</option>
          )}
          {snapshots.map(s => (
            <option key={s.period} value={s.period}>
              {periodLabel(s.period)}{s.locked ? " 🔒" : ""}
            </option>
          ))}
        </select>
        <button onClick={() => refreshFromQb(period)} disabled={!!busy || !period || !qbConnected} style={S.btnPrimary}>
          {busy === "refreshing" ? "Pulling…" : "Refresh from QB"}
        </button>
        <button onClick={backfillFy} disabled={!!busy || !!backfill || !period || !qbConnected} style={S.btn}>
          {backfill ? `Backfilling ${backfill.done}/${backfill.total}…` : "Backfill fiscal year"}
        </button>
        <button
          onClick={async () => {
            if (!period) return;
            setBusy("refreshing-jobber"); setError(""); setStatus("");
            try {
              // Heavy step: pull the last 30 days of invoices from Jobber into jb_invoices.
              // jobber-sync is on a 300s timeout so we wait for it.
              const r1 = await apiFetch("/api/jobber-sync?only=invoices&days=30").then(r => r.json());
              if (r1?.results?.invoices?.error) throw new Error("Jobber sync: " + r1.results.invoices.error);
              // Cheap step: recompute the AR breakdown off jb_invoices and stamp it onto the snapshot.
              const r2 = await apiFetch(API, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ action: "recompute-jobber-ar", period }),
              }).then(r => r.json());
              if (!r2.ok) throw new Error(r2.error || "Recompute failed");
              const sres = await apiFetch(`${API}?action=get-snapshot&period=${period}`).then(r => r.json());
              setSnapshot(sres?.snapshot || null);
              const upserted = r1?.results?.invoices?.upserted ?? r1?.results?.invoices ?? "?";
              setStatus(`Jobber refreshed — ${upserted} invoice(s) synced, AR now ${r2.jobber_ar?.outstanding?.toLocaleString("en-CA", { style: "currency", currency: "CAD", maximumFractionDigits: 0 })}.`);
            } catch (e) {
              setError(e.message);
            } finally {
              setBusy("");
            }
          }}
          disabled={!!busy || !period}
          style={S.btn}
        >
          {busy === "refreshing-jobber" ? "Syncing…" : "Refresh Jobber"}
        </button>
        {budget && (
          <>
            <button onClick={() => setShowBudget(true)} style={S.btn}>Edit Budget</button>
            <button onClick={() => setShowMapping(true)} style={S.btn}>Account Mapping</button>
            <button
              onClick={async () => {
                if (!period) return;
                setBusy("loading"); setError("");
                try {
                  const r = await apiFetch(`${API}?action=audit&period=${period}`).then(r => r.json());
                  if (!r.ok) throw new Error(r.error || "Audit failed");
                  setAudit(r);
                  setShowAudit(true);
                } catch (e) { setError(e.message); }
                finally { setBusy(""); }
              }}
              style={S.btn}
              disabled={!!busy || !snapshot}
            >
              Audit
            </button>
            <button onClick={openReport} style={S.btnPrimary} disabled={!!busy || (!baseline && !computed)}>
              Generate Report
            </button>
          </>
        )}
        <PeriodQuickPick currentPeriod={period} onPick={p => { setPeriod(p); refreshFromQb(p); }} />
      </div>

      {planningMode && (
        <PlannerPanel
          baseline={baseline}
          plan={plan}
          budget={budget}
          scenario={activeScenario}
          setScenario={setActiveScenario}
          scenarios={scenarios}
          onLoad={loadScenario}
          onSave={saveScenario}
          onDelete={deleteScenario}
          aiSummary={aiSummary}
          runAi={runAi}
          busy={planBusy}
          employees={employees}
          updateEmployee={updateEmployee}
          saveRoster={saveRoster}
          rosterBusy={rosterBusy}
          setWage={setWage}
          setBonus={setBonus}
          setBurden={setBurden}
        />
      )}

      {!snapshot && !busy && (
        <div style={S.emptyCard}>
          <div style={S.emptyTitle}>No snapshot for {periodLabel(period)}</div>
          <div style={S.emptyText}>
            {qbConnected === false
              ? "Connect QuickBooks to begin."
              : !budget
                ? "Click “Run Setup” to seed defaults, then “Refresh from QB” to pull this month."
                : "Click “Refresh from QB” to pull this month."}
          </div>
        </div>
      )}

      {snapshot && computed && computed.stale && (
        <div style={S.cardWarning}>
          <div style={S.cardHeader}>
            <div style={S.cardTitle}>This period was pulled before the BS classification fix shipped.</div>
            <button onClick={() => refreshFromQb(period)} disabled={!!busy} style={S.btnPrimary}>
              {busy === "refreshing" ? "Pulling…" : "Re-pull from QB"}
            </button>
          </div>
          <div style={S.cardSubtitle}>
            Cash / AR / AP / Quick Ratio shown below are zeroed out until you refresh — old regex-based extraction
            produced wrong numbers and has been removed. P&L variance is still accurate.
          </div>
        </div>
      )}

      {snapshot && !computed?.stale && period === currentPeriod() && hoursSince(snapshot.refreshed_at) > 12 && (
        <div style={S.cardWarning}>
          <div style={S.cardHeader}>
            <div style={S.cardTitle}>
              Snapshot is {Math.floor(hoursSince(snapshot.refreshed_at) / 24)} day(s) old —
              QuickBooks has likely changed since.
            </div>
            <button onClick={() => refreshFromQb(period)} disabled={!!busy} style={S.btnPrimary}>
              {busy === "refreshing" ? "Pulling…" : "Refresh now"}
            </button>
          </div>
          <div style={S.cardSubtitle}>
            Cash, AR, AP and the {periodLabel(period)} P&amp;L below reflect data as of{" "}
            {snapshot.refreshed_at?.slice(0, 16)?.replace("T", " ")} UTC. For an accurate meeting,
            refresh first.
          </div>
        </div>
      )}

      {snapshot && computed && (
        <>
          {/* KPI hero */}
          <div style={S.kpiGrid}>
            <Kpi label="Cash (booked in QB)" value={fmtMoney(computed.bs.cash)}
                 sub={`Δ ${fmtMoney(computed.bs.cash - computed.bsPrior.cash)} vs prior · check QB Banking → For Review for pending bank items`} />
            {computed.jobberAr ? (
              <Kpi
                label={`AR (Jobber) · ${computed.jobberAr.unpaid_count} unpaid`}
                value={fmtMoney(computed.jobberAr.outstanding)}
                sub={
                  <>
                    <span style={{ color: "#fca5a5" }}>
                      {fmtMoney(computed.jobberAr.overdue?.amount || 0)} overdue
                      ({computed.jobberAr.overdue?.count || 0})
                    </span>
                    {" · "}
                    <span style={{ color: "#86efac" }}>
                      {fmtMoney(computed.jobberAr.not_yet_due?.amount || 0)} not yet due
                      ({computed.jobberAr.not_yet_due?.count || 0})
                    </span>
                    <br />
                    QB books: {fmtMoney(computed.bs.ar)}
                  </>
                }
                highlight={Math.abs(computed.bs.ar - computed.jobberAr.outstanding) > 5000 ? "ok" : undefined}
              />
            ) : (
              <Kpi label="AR (QB)" value={fmtMoney(computed.bs.ar)}
                   sub={`Δ ${fmtMoney(computed.bs.ar - computed.bsPrior.ar)} vs prior`} />
            )}
            <Kpi label="AP + CC + LOC" value={fmtMoney(computed.quickLiab)} sub={`AP ${fmtMoney(computed.bs.ap)} · CC ${fmtMoney(computed.bs.credit_cards)}`} />
            <Kpi label="Quick Ratio" value={computed.quickRatio?.toFixed(2) ?? "—"}
                 sub={`uses ${computed.arSource} AR (${fmtMoney(computed.arNow)})${computed.quickRatioPrior != null ? ` · prior ${computed.quickRatioPrior.toFixed(2)} (QB)` : ""}`}
                 highlight={computed.quickRatio >= 3 ? "good" : computed.quickRatio >= 2 ? "ok" : "bad"} />
            <Kpi label={`${viewMode === "fy" ? "FY" : viewMode === "quarter" ? "Quarter" : "MTD"} Revenue`} value={fmtMoney(computed.qbRev)}
                 sub={`vs market ${fmtMoney(computed.monthBudgetRevenue)} (${fmtChange((computed.qbRev - computed.monthBudgetRevenue) / (computed.monthBudgetRevenue || 1))})`} />
            <Kpi label="YTD Revenue" value={fmtMoney(computed.qbYtdRev)}
                 sub={`vs ${fmtMoney(computed.ytdBudgetRevenue)} (${fmtChange((computed.qbYtdRev - computed.ytdBudgetRevenue) / (computed.ytdBudgetRevenue || 1))})`} />
            <Kpi label={`${viewMode === "fy" ? "FY" : viewMode === "quarter" ? "Quarter" : "MTD"} Net Profit`} value={fmtMoney(computed.qbNet)}
                 sub={`${fmtPct(computed.qbRev > 0 ? computed.qbNet / computed.qbRev : 0)} margin · QuickBooks net`}
                 highlight={computed.qbNet > computed.monthNetBudget ? "good" : "bad"} />
            <Kpi label="YTD Net Profit" value={fmtMoney(computed.qbYtdNet)}
                 sub={`${fmtPct(computed.qbYtdRev > 0 ? computed.qbYtdNet / computed.qbYtdRev : 0)} margin · target ${fmtPct(budget?.net_profit_target_pct || 0.09)}`}
                 highlight={computed.qbYtdNet > computed.ytdNetBudget ? "good" : "bad"} />
          </div>

          {/* Coverage note for Quarter / FY roll-ups so a partial range never looks complete */}
          {viewMode !== "month" && (computed.missingMonths?.length > 0) && (
            <div style={S.cardWarning}>
              <div style={S.cardHeader}>
                <div style={S.cardTitle}>
                  Showing {computed.effectiveCount} of {computed.months.filter(k => k <= currentPeriod()).length} month(s)
                  in {rangeLabel(viewMode, period, quarter)}
                </div>
                <button onClick={backfillFy} disabled={!!busy || !!backfill || !qbConnected} style={S.btn}>
                  {backfill ? `Backfilling ${backfill.done}/${backfill.total}…` : "Backfill fiscal year"}
                </button>
              </div>
              <div style={S.cardSubtitle}>
                Missing from QuickBooks: {computed.missingMonths.map(periodLabel).join(", ")}.
              </div>
            </div>
          )}

          {/* Charts — cost structure vs market, trend, composition, salaries */}
          <ChartsBand computed={computed} rangeText={rangeLabel(viewMode, period, quarter)} />

          {/* Forward view — self-updating 6-month forecast (QB-grounded) */}
          <ForecastBand />

          {/* Financial health — cash/runway, AR aging, client concentration (live) */}
          <HealthBand />
          {budget?.reclass?.length > 0 && viewMode !== "fy" && (
            <div style={{ ...S.cardSubtitle, margin: "-8px 0 12px" }}>
              {budget.reclass.length} manual reclass correction(s) apply to the Fiscal-Year view only — switch to <b>Fiscal Year</b> to see them reflected.
            </div>
          )}

          {/* Variance heatmap (Range + YTD side by side) */}
          <VarianceTable
            title={`${rangeLabel(viewMode, period, quarter)} (Actual vs Market)`}
            ytdTitle={`YTD (FY ending May ${fiscalYearForPeriod(period)})`}
            computed={computed}
          />

          {/* Balance sheet + ratios */}
          <BalanceSheetCard computed={computed} period={period} />

          {/* Marketing vendors */}
          <MarketingCard snapshot={snapshot} />

          {/* Balance Sheet rows we couldn't classify — surface them so they
              don't silently distort the quick ratio. */}
          {computed.bs.unclassified?.length > 0 && (
            <div style={S.cardWarning}>
              <div style={S.cardHeader}>
                <div style={S.cardTitle}>{computed.bs.unclassified.length} balance-sheet account(s) unclassified</div>
              </div>
              <div style={S.cardSubtitle}>
                These weren't picked up as Bank/AR/AP/CC by account-type lookup OR section-header matching.
                If they belong in your quick-ratio numerator or denominator, the QB AccountType may need correcting:
              </div>
              <table style={S.bsTable}>
                <thead><tr>
                  <th style={{ ...S.th, textAlign: "left" }}>Account</th>
                  <th style={{ ...S.th, textAlign: "left" }}>QB Path</th>
                  <th style={S.th}>Amount</th>
                </tr></thead>
                <tbody>
                  {computed.bs.unclassified.slice(0, 20).map((r, i) => (
                    <tr key={i}>
                      <td style={S.tdLabel}>{r.name}</td>
                      <td style={S.tdMuted}>{r.path}</td>
                      <td style={S.td}>{fmtMoney(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Account mapping check — show counts of unmapped */}
          <UnmappedAccountsCard rows={bucketRows} onClickEdit={() => setShowMapping(true)} />

          <div style={S.metaFoot}>
            Last refreshed {snapshot.refreshed_at?.slice(0, 16)?.replace("T", " ")} UTC ·
            {snapshot.locked ? " locked" : " unlocked"} ·
            <button style={S.linkBtn} onClick={async () => {
              await apiFetch(API, { method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ action: "lock-snapshot", period, locked: !snapshot.locked }) });
              const r = await apiFetch(`${API}?action=get-snapshot&period=${period}`).then(r => r.json());
              setSnapshot(r?.snapshot || null);
            }}>{snapshot.locked ? "Unlock" : "Lock"} this period</button>
          </div>
        </>
      )}

      {showBudget && budget && (
        <BudgetEditor
          budget={budget}
          onClose={() => setShowBudget(false)}
          onSave={saveBudget}
          onImportWeights={async () => {
            setBusy("saving"); setError(""); setStatus("");
            try {
              const r = await apiFetch(API, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ action: "import-historical-weights", year: budget.year }),
              }).then(r => r.json());
              if (!r.ok) throw new Error(r.error || "Import failed");
              const bres = await apiFetch(`${API}?action=get-budget&year=${budget.year}`).then(r => r.json());
              setBudget(bres?.budget || null);
              setStatus(`Weights imported from prior fiscal year revenue (${fmtMoney(r.total_revenue)}).`);
            } catch (e) { setError(e.message); }
            finally { setBusy(""); }
          }}
          busy={!!busy}
        />
      )}

      {showMapping && (
        <BucketMappingEditor
          rows={bucketRows}
          onClose={() => setShowMapping(false)}
          onSave={saveBucketMap}
          busy={!!busy}
        />
      )}

      {showAudit && audit && (
        <AuditDrawer audit={audit} onClose={() => setShowAudit(false)} />
      )}
    </div>
  );
}

// ── Audit drawer ─────────────────────────────────────────────────────────
// Shows the raw account-level data behind every dashboard number, so we
// can verify QB → coach math line by line.
function AuditDrawer({ audit, onClose }) {
  const sections = [
    { key: "cash",         label: "Cash (Bank Accounts)" },
    { key: "ar",           label: "Accounts Receivable" },
    { key: "ap",           label: "Accounts Payable" },
    { key: "credit_cards", label: "Credit Cards" },
    { key: "loc",          label: "Lines of Credit" },
    { key: "ignored_for_quick_ratio", label: "Other (not in quick ratio)" },
    { key: "unclassified", label: "Unclassified — needs QB AccountType" },
  ];
  const jb = audit.jobber_ar;
  return (
    <div style={S.modalBackdrop} onClick={onClose}>
      <div style={{ ...S.modal, maxWidth: 920 }} onClick={e => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <div style={S.cardTitle}>Audit — {audit.period} (refreshed {audit.refreshed_at?.slice(0, 16)?.replace("T", " ")})</div>
          <button onClick={onClose} style={S.iconBtn}>×</button>
        </div>
        <div style={S.modalBody}>
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
            <Kpi label="QB AR (BS)"     value={fmtMoney(audit.bs_totals?.ar || 0)} />
            <Kpi
              label="Jobber AR (live)"
              value={fmtMoney(jb?.outstanding || 0)}
              sub={
                <>
                  <span style={{ color: "#fca5a5" }}>{fmtMoney(jb?.overdue?.amount || 0)} overdue ({jb?.overdue?.count || 0})</span>
                  {" · "}
                  <span style={{ color: "#86efac" }}>{fmtMoney(jb?.not_yet_due?.amount || 0)} not yet due ({jb?.not_yet_due?.count || 0})</span>
                  <br />
                  {jb?.unpaid_count || 0} unpaid · {jb?.total_invoices || 0} total in jb_invoices
                </>
              }
            />
            {jb?.aging && (
              <Kpi
                label="Aging — overdue"
                value={fmtMoney(jb.overdue?.amount || 0)}
                sub={
                  <>
                    1–30d: {fmtMoney(jb.aging.d0_30.amount)} ({jb.aging.d0_30.count})<br />
                    31–60d: {fmtMoney(jb.aging.d31_60.amount)} ({jb.aging.d31_60.count})<br />
                    61–90d: {fmtMoney(jb.aging.d61_90.amount)} ({jb.aging.d61_90.count})<br />
                    90d+: {fmtMoney(jb.aging.d90_plus.amount)} ({jb.aging.d90_plus.count})
                  </>
                }
              />
            )}
            <Kpi label="Cash"           value={fmtMoney(audit.bs_totals?.cash || 0)} />
            <Kpi label="AP"             value={fmtMoney(audit.bs_totals?.ap || 0)} />
            <Kpi label="Credit Cards"   value={fmtMoney(audit.bs_totals?.credit_cards || 0)} />
          </div>

          <div style={{ fontWeight: 700, fontSize: 13, marginTop: 16, marginBottom: 8, color: "#a5ce3a" }}>
            BALANCE SHEET — END-OF-PERIOD
          </div>
          {sections.map(s => {
            const data = audit.bs_by_bucket[s.key];
            if (!data || !data.accounts.length) return null;
            return (
              <div key={s.key} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, textTransform: "uppercase", color: "#8892a4", letterSpacing: "0.05em", marginBottom: 4 }}>
                  {s.label} — {fmtMoney(data.total)}
                </div>
                <table style={S.bsTable}>
                  <thead><tr>
                    <th style={{ ...S.th, textAlign: "left" }}>Account</th>
                    <th style={{ ...S.th, textAlign: "left" }}>QB Type</th>
                    <th style={{ ...S.th, textAlign: "left" }}>Path</th>
                    <th style={S.th}>Amount</th>
                  </tr></thead>
                  <tbody>
                    {data.accounts.map((a, i) => (
                      <tr key={i}>
                        <td style={S.tdLabel}>{a.name}</td>
                        <td style={S.tdMuted}>{a.account_type || "—"}</td>
                        <td style={S.tdMuted}>{a.path}</td>
                        <td style={S.td}>{fmtMoney(a.amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}

          <div style={{ fontWeight: 700, fontSize: 13, marginTop: 24, marginBottom: 8, color: "#a5ce3a" }}>
            JOBBER AR — TOP 20 UNPAID INVOICES
          </div>
          <table style={S.bsTable}>
            <thead><tr>
              <th style={{ ...S.th, textAlign: "left" }}>Invoice #</th>
              <th style={{ ...S.th, textAlign: "left" }}>Client</th>
              <th style={{ ...S.th, textAlign: "left" }}>Due</th>
              <th style={S.th}>Invoiced</th>
              <th style={S.th}>Outstanding</th>
            </tr></thead>
            <tbody>
              {(audit.jobber_ar_top_unpaid || []).map((r, i) => (
                <tr key={i}>
                  <td style={S.tdLabel}>{r.invoice_num || r.invoice_number || "—"}</td>
                  <td style={S.tdMuted}>{r.client_name || "—"}</td>
                  <td style={S.tdMuted}>{r.due_date || "—"}</td>
                  <td style={S.td}>{fmtMoney(r.amounts_invoiced)}</td>
                  <td style={S.td}>{fmtMoney(r.amounts_outstanding)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div style={{ fontWeight: 700, fontSize: 13, marginTop: 24, marginBottom: 8, color: "#a5ce3a" }}>
            P&L BREAKDOWN — EVERY ACCOUNT WITH ITS BUCKET ASSIGNMENT
          </div>
          <table style={S.bsTable}>
            <thead><tr>
              <th style={{ ...S.th, textAlign: "left" }}>Account</th>
              <th style={{ ...S.th, textAlign: "left" }}>QB Group</th>
              <th style={{ ...S.th, textAlign: "left" }}>Bucket</th>
              <th style={S.th}>Month</th>
              <th style={S.th}>YTD</th>
            </tr></thead>
            <tbody>
              {(audit.pl_breakdown || []).map((r, i) => (
                <tr key={i} style={r.auto ? { background: "rgba(245,158,11,0.04)" } : null}>
                  <td style={S.tdLabel}>{r.name}{r.auto ? " ⚠" : ""}</td>
                  <td style={S.tdMuted}>{r.parent}</td>
                  <td style={S.tdMuted}>{r.bucket}</td>
                  <td style={S.td}>{fmtMoney(r.month)}</td>
                  <td style={S.tdMuted}>{fmtMoney(r.ytd)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={S.modalFooter}>
          <button onClick={onClose} style={S.btnPrimary}>Close</button>
        </div>
      </div>
    </div>
  );
}

// ── Scenario planner: buckets, tax math ────────────────────────────────────
const VARIABLE_BUCKETS = DIRECT_COST_BUCKETS;        // scale with revenue
const BUCKET_OPTIONS = [...DIRECT_COST_BUCKETS, ...OVERHEAD_BUCKETS, "non_opex"];

// 2025 BC + Federal personal income tax (editable in the UI). Salary-only estimate.
const TAX_2025 = {
  federal: [[57375, 0.15], [114750, 0.205], [177882, 0.26], [253414, 0.29], [Infinity, 0.33]],
  federalBPA: 16129,
  bc: [[49279, 0.0506], [98560, 0.077], [113158, 0.105], [137407, 0.1229], [186306, 0.147], [259829, 0.168], [Infinity, 0.205]],
  bcBPA: 12932,
  cppMax: 4034, eiMax: 1077,
  // Non-eligible (small-business / CCPC) dividend params, 2025.
  divGrossUp: 0.15, divFedDTC: 0.090301, divBcDTC: 0.0196,
};
function progressiveTax(income, brackets) {
  let tax = 0, prev = 0;
  for (const [cap, rate] of brackets) {
    const amt = Math.min(income, cap) - prev;
    if (amt > 0) tax += amt * rate;
    prev = cap;
    if (income <= cap) break;
  }
  return tax;
}
function incomeTax(gross, a = TAX_2025) {
  const fed = Math.max(0, progressiveTax(gross, a.federal) - a.federalBPA * a.federal[0][1]);
  const bc  = Math.max(0, progressiveTax(gross, a.bc) - a.bcBPA * a.bc[0][1]);
  const cpp = Math.min(a.cppMax, Math.max(0, gross - 3500) * 0.0595);
  const ei  = Math.min(a.eiMax, gross * 0.0164);
  return { fed, bc, cpp, ei, total: fed + bc + cpp + ei };
}
const netFromGross = (g, a) => g - incomeTax(g, a).total;
function grossForNet(targetNet, a = TAX_2025) {
  let lo = 0, hi = targetNet * 2 + 60000;
  for (let i = 0; i < 60; i++) {
    const mid = (lo + hi) / 2;
    if (netFromGross(mid, a) < targetNet) lo = mid; else hi = mid;
  }
  return (lo + hi) / 2;
}
// Personal tax on a non-eligible dividend, stacked MARGINALLY on top of the
// salary (otherIncome) that person already draws, net of the fed + BC dividend
// tax credits. CPP/EI don't apply to dividends.
function dividendTax(dividend, otherIncome, a = TAX_2025) {
  if (dividend <= 0) return { tax: 0, net: 0, rate: 0 };
  const grossed = dividend * (1 + a.divGrossUp);
  const fedMarg = progressiveTax(otherIncome + grossed, a.federal) - progressiveTax(otherIncome, a.federal);
  const bcMarg  = progressiveTax(otherIncome + grossed, a.bc)      - progressiveTax(otherIncome, a.bc);
  const tax = Math.max(0, fedMarg - grossed * a.divFedDTC) + Math.max(0, bcMarg - grossed * a.divBcDTC);
  return { tax, net: dividend - tax, rate: tax / dividend };
}

// ── Employee roster math ───────────────────────────────────────────────────
// Standard rate = current (no delta). Planned rate (scenario.wages[name]) makes
// the delta that flows into the P&L. Salary rate is monthly ($×12); hourly is
// $/hr × annual hours (override → timesheet actual → 2080 fallback).
function rosterWages(employees, wages, bonuses, burdenPct) {
  const w = wages || {}; const bz = bonuses || {};
  // Incremental burden (CPP/EI/WCB) on RAISES only — baseline burden is already
  // in the QB actuals. Default ~3%: most full-timers are over the CPP/EI annual
  // cap, so a raise only adds WCB + benefits. Editable.
  const burden = 1 + (burdenPct != null && burdenPct !== "" ? Number(burdenPct) : 3) / 100;
  const out = {
    perEmployee: [], deltas: { owner_wages: 0, labor: 0, admin_wages: 0, marketing: 0, vehicle: 0, other_indirects: 0 },
    ownerStdAnnual: 0, ownerPlanAnnual: 0, totalStdAnnual: 0, totalPlanAnnual: 0,
    browningsStdMo: 0, browningsPlanMo: 0, coltonStdMo: 0, coltonPlanMo: 0, coltonOfficePct: 25,
  };
  for (const e of (employees || [])) {
    if (e.active === false) continue;
    const salary = e.pay_type === "salary";
    const hours = salary ? null
      : (e.hours_override != null && e.hours_override !== "" ? Number(e.hours_override)
        : (e.actual_hours != null ? Number(e.actual_hours) : 2080));
    const stdRate = Number(e.rate) || 0;
    const plannedRate = (w[e.employee_name] != null && w[e.employee_name] !== "") ? Number(w[e.employee_name]) : stdRate;
    const baseCost = salary ? stdRate * 12 : stdRate * hours;
    const planCost = salary ? plannedRate * 12 : plannedRate * hours;
    const bonusAnnual = (bz[e.employee_name] != null && bz[e.employee_name] !== "") ? Number(bz[e.employee_name]) * 12 : 0;
    const wageDelta = planCost - baseCost;
    const delta = (wageDelta + bonusAnnual) * burden;   // loaded cost added to P&L
    const officePct = (e.office_pct != null && e.office_pct !== "") ? Number(e.office_pct) : null;
    if (e.bucket === "owner_wages" && officePct != null) {
      out.deltas.owner_wages += delta * (officePct / 100);
      out.deltas.labor       += delta * (1 - officePct / 100);
    } else {
      out.deltas[e.bucket] = (out.deltas[e.bucket] || 0) + delta;
    }
    out.totalStdAnnual += baseCost; out.totalPlanAnnual += planCost + bonusAnnual;
    if (e.bucket === "owner_wages") { out.ownerStdAnnual += baseCost; out.ownerPlanAnnual += planCost; }
    const fn = (e.first_name || "").toLowerCase();
    if (fn === "dylan" || fn === "allie") { out.browningsStdMo += baseCost / 12; out.browningsPlanMo += planCost / 12; }
    if (fn === "colton") { out.coltonStdMo += baseCost / 12; out.coltonPlanMo += planCost / 12; if (officePct != null) out.coltonOfficePct = officePct; }
    out.perEmployee.push({ name: e.employee_name, fn, payType: e.pay_type, hours, stdRate, plannedRate, baseCost, planCost, bonusAnnual, delta, bucket: e.bucket, officePct, actualHours: e.actual_hours });
  }
  return out;
}

// ── Charts band ─────────────────────────────────────────────────────────
function ChartsBand({ computed, rangeText }) {
  const c = computed;
  const benchTip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <div style={S.chartTip}>
        <div style={{ fontWeight: 700, marginBottom: 2 }}>{d.label}</div>
        <div>Actual (adjusted): {d.actual}% ({fmtMoney(d.amount)})</div>
        {d.qb !== d.actual && <div style={{ color: "#a78bfa" }}>Per QuickBooks: {d.qb}%</div>}
        <div style={{ color: CHART.target }}>Market: {d.target}%</div>
      </div>
    );
  };
  const hasComp = c.compositionData?.length > 0;
  const trendData = (c.trend || []).filter(t => t.hasData);
  return (
    <div style={S.chartsGrid}>
      {/* 1. Cost structure vs market */}
      <div style={{ ...S.card, marginBottom: 0 }}>
        <div style={S.cardHeader}><div style={S.cardTitle}>Cost Structure vs Market — {rangeText}</div></div>
        <div style={S.cardSubtitle}>Each line as % of revenue. Green = QB actual (red = over market) · purple = re-allocated in ElecBid · grey = moved out vs QB · blue = market.</div>
        <div style={{ width: "100%", height: 330, marginTop: 8 }}>
          <ResponsiveContainer>
            <BarChart data={c.benchmarkData} layout="vertical" margin={{ left: 18, right: 28, top: 4, bottom: 4 }}>
              <CartesianGrid horizontal={false} stroke={CHART.grid} />
              <XAxis type="number" tick={{ fill: CHART.axis, fontSize: 11 }} tickFormatter={v => `${v}%`} />
              <YAxis type="category" dataKey="label" width={96} tick={{ fill: CHART.axis, fontSize: 11 }} />
              <Tooltip content={benchTip} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
              <Legend wrapperStyle={{ fontSize: 11, color: CHART.axis }} />
              <Bar dataKey="qbBase" stackId="act" name="QB actual" barSize={9}>
                {c.benchmarkData.map((d, i) => {
                  const bad = d.isCost ? d.actual > d.target : d.actual < d.target;
                  return <Cell key={i} fill={bad ? CHART.over : CHART.under} />;
                })}
              </Bar>
              <Bar dataKey="added"   stackId="act" name="Re-allocated (ElecBid)" fill="#a78bfa" barSize={9} />
              <Bar dataKey="removed" stackId="act" name="Moved out vs QB" fill="rgba(124,138,165,0.45)" radius={[0, 3, 3, 0]} barSize={9} />
              <Bar dataKey="target"  stackId="mkt" name="Market" fill={CHART.target} radius={[0, 3, 3, 0]} barSize={9} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 2. Revenue & net profit trend across the fiscal year */}
      <div style={{ ...S.card, marginBottom: 0 }}>
        <div style={S.cardHeader}><div style={S.cardTitle}>Revenue &amp; Net Profit — fiscal year</div></div>
        <div style={S.cardSubtitle}>Monthly revenue (bars) and net margin % (line). Months without a snapshot are skipped.</div>
        <div style={{ width: "100%", height: 330, marginTop: 8 }}>
          {trendData.length ? (
            <ResponsiveContainer>
              <ComposedChart data={trendData} margin={{ left: 6, right: 6, top: 8, bottom: 4 }}>
                <CartesianGrid stroke={CHART.grid} vertical={false} />
                <XAxis dataKey="label" tick={{ fill: CHART.axis, fontSize: 11 }} />
                <YAxis yAxisId="l" tick={{ fill: CHART.axis, fontSize: 11 }} tickFormatter={v => `$${Math.round(v / 1000)}k`} />
                <YAxis yAxisId="r" orientation="right" tick={{ fill: CHART.axis, fontSize: 11 }} tickFormatter={v => `${v}%`} />
                <Tooltip contentStyle={S.chartTip} formatter={(v, n) => (n === "Net margin %" ? `${v}%` : fmtMoney(v))} />
                <Legend wrapperStyle={{ fontSize: 11, color: CHART.axis }} />
                <Bar yAxisId="l" dataKey="revenue" name="Revenue" fill={CHART.actual} radius={[3, 3, 0, 0]} barSize={18} />
                <Line yAxisId="r" dataKey="margin" name="Net margin %" stroke={CHART.net} strokeWidth={2} dot={{ r: 3 }} />
              </ComposedChart>
            </ResponsiveContainer>
          ) : <div style={S.emptyText}>No monthly data yet — Backfill the fiscal year to populate the trend.</div>}
        </div>
      </div>

      {/* 3. Where the revenue goes */}
      <div style={{ ...S.card, marginBottom: 0 }}>
        <div style={S.cardHeader}><div style={S.cardTitle}>Where the Revenue Goes — {rangeText}</div></div>
        <div style={{ width: "100%", height: 330, marginTop: 8 }}>
          {hasComp ? (
            <ResponsiveContainer>
              <PieChart>
                <Pie data={c.compositionData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={62} outerRadius={104} paddingAngle={1}>
                  {c.compositionData.map((d, i) => <Cell key={i} fill={SLICE_COLORS[d.key] || "#7c8aa5"} />)}
                </Pie>
                <Tooltip contentStyle={S.chartTip} formatter={(v, n) => [fmtMoney(v), n]} />
                <Legend wrapperStyle={{ fontSize: 11, color: CHART.axis }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div style={S.emptyText}>No cost data for this range.</div>}
        </div>
      </div>

      {/* 4. Salaries vs market */}
      <SalaryCard salary={c.salary} />
    </div>
  );
}

// Self-updating forward forecast — pulls /api/coach?action=forecast (QB-grounded
// revenue + seasonality, live Jobber pipeline, lead-flow) and renders the cone.
function ForecastBand() {
  const [fc, setFc] = useState(null);
  const [err, setErr] = useState("");
  const [extraMktg, setExtraMktg] = useState(0);  // marketing lever: extra $/mo
  const [mult, setMult] = useState(2.25);          // gross revenue per incremental $1 (default just below break-even)
  useEffect(() => {
    let alive = true;
    apiFetch(`${API}?action=forecast`)
      .then(r => r.json())
      .then(j => { if (alive) { if (j?.ok) setFc(j); else setErr(j?.error || "forecast unavailable"); } })
      .catch(() => { if (alive) setErr("forecast unavailable"); });
    return () => { alive = false; };
  }, []);

  const monLabel = ym => {
    const [y, m] = (ym || "").split("-");
    const names = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${names[+m] || ym}’${(y || "").slice(2)}`;
  };

  if (err) return (
    <div style={{ ...S.card, marginBottom: 0 }}>
      <div style={S.cardHeader}><div style={S.cardTitle}>Forward View — 6-month forecast</div></div>
      <div style={S.emptyText}>Forecast unavailable ({err}). Needs a live QuickBooks connection.</div>
    </div>
  );
  if (!fc) return (
    <div style={{ ...S.card, marginBottom: 0 }}>
      <div style={S.cardHeader}><div style={S.cardTitle}>Forward View — 6-month forecast</div></div>
      <div style={S.emptyText}>Loading forecast…</div>
    </div>
  );

  const recentActuals = (fc.actuals || []).slice(-13); // ~last year of QB months
  const d = fc.drivers || {};
  const t = fc.totals || {};
  const be = fc.economics?.breakeven_monthly || 0;
  const cm = fc.economics?.contribution_margin || 0.39;
  // Full $127K/yr raise-plan overlay (mirrors the scenario planner's stack): −$10,605/mo
  const RAISE_MO = 10605;
  const sixRaise = RAISE_MO * (fc.horizon || 6);
  const netRaises = (t.net_likely_current_cost || 0) - sixRaise;
  const marRaises = t.likely ? (netRaises / t.likely) * 100 : 0;

  // ── Marketing lever (client-side, conservative incremental model) ──
  // Honest: incremental paid marketing at current scale is ~break-even — Browning's
  // 6× growth came with flat ~5% marketing-%, i.e. referral/repeat-driven, not paid.
  const CUR_MKTG_MO = 9900;                       // ~5.1% of FY revenue ÷ 12 (all 4 ad accounts)
  const beMult = +(1 / cm).toFixed(2);             // ≈ $2.56 gross/$1 just to cover the spend
  const drCap = 1 / (1 + Math.max(0, extraMktg - CUR_MKTG_MO) / CUR_MKTG_MO * 0.5); // diminishing returns past ~current spend
  const multEff = mult * drCap;
  const lever = (fc.forecast || []).map((f, i) => {
    const lag = Math.max(0, Math.min(1, (i + 1 - 2 + 1) / 2)); // LAG=2, RAMP=2 → [0,.5,1,1,1,1]
    const upRev = extraMktg * multEff * lag;
    return { upRev, upNet: upRev * cm - extraMktg };
  });
  const sumUpRev = lever.reduce((a, o) => a + o.upRev, 0);
  const sumUpNet = lever.reduce((a, o) => a + o.upNet, 0);
  let cum = 0, payback = null;
  lever.forEach((o, i) => { cum += o.upNet; if (payback === null && cum >= 0) payback = i + 1; });
  const hasLever = extraMktg > 0;

  const data = [
    ...recentActuals.map(a => ({ label: monLabel(a.month), revenue: a.revenue })),
    ...(fc.forecast || []).map((f, i) => ({
      label: monLabel(f.month), likely: f.likely, low: f.low, high: f.high, band: [f.low, f.high],
      likelyMkt: hasLever ? Math.round(f.likely + lever[i].upRev) : null,
    })),
  ];

  const Stat = ({ label, value, sub, color }) => (
    <div style={{ flex: "1 1 120px", minWidth: 120, padding: "8px 10px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8 }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, color: CHART.axis }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2, color: color || "inherit" }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: CHART.axis, marginTop: 1 }}>{sub}</div>}
    </div>
  );

  return (
    <div style={{ ...S.card, marginBottom: 16 }}>
      <div style={S.cardHeader}><div style={S.cardTitle}>Forward View — 6-month forecast (projection, not plan)</div></div>
      <div style={S.cardSubtitle}>
        Authoritative monthly revenue + seasonality from QuickBooks; near-term confidence from the live Jobber pipeline (open quotes at {d.win_rate}% win + booked jobs); lead-flow as the leading indicator. Solid bars = actual · green line = likely · dashed = conservative/optimistic cone · red = break-even. As of {fc.as_of}.
      </div>
      <div style={{ width: "100%", height: 340, marginTop: 8 }}>
        <ResponsiveContainer>
          <ComposedChart data={data} margin={{ left: 6, right: 6, top: 8, bottom: 4 }}>
            <CartesianGrid stroke={CHART.grid} vertical={false} />
            <XAxis dataKey="label" tick={{ fill: CHART.axis, fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={50} />
            <YAxis tick={{ fill: CHART.axis, fontSize: 11 }} tickFormatter={v => `$${Math.round(v / 1000)}k`} />
            <Tooltip contentStyle={S.chartTip} formatter={(v, n) => [fmtMoney(v), n]} />
            <Legend wrapperStyle={{ fontSize: 11, color: CHART.axis }} />
            <Area type="monotone" dataKey="band" name="Conservative–optimistic" stroke="none" fill="rgba(34,197,94,0.12)" />
            <Bar dataKey="revenue" name="Actual (QB)" fill={CHART.actual} radius={[3, 3, 0, 0]} barSize={14} />
            <Line dataKey="likely" name="Likely" stroke={CHART.net} strokeWidth={3} dot={{ r: 3 }} connectNulls />
            <Line dataKey="low" name="Conservative" stroke={CHART.over} strokeWidth={1.5} strokeDasharray="5 4" dot={false} connectNulls />
            <Line dataKey="high" name="Optimistic" stroke="rgba(34,197,94,0.6)" strokeWidth={1.5} strokeDasharray="5 4" dot={false} connectNulls />
            {hasLever && <Line dataKey="likelyMkt" name="Likely + marketing" stroke="#ec4899" strokeWidth={2} strokeDasharray="2 3" dot={{ r: 2 }} connectNulls />}
            <ReferenceLine y={be} stroke={CHART.over} strokeDasharray="3 4" label={{ value: `break-even ~${fmtMoney(be)}`, fill: CHART.over, fontSize: 10, position: "insideBottomRight" }} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
        <Stat label="Run-rate (QB t12)" value={`${fmtMoney(d.run_rate_monthly)}/mo`} sub={`vs scenario plan's $203k`} />
        <Stat label="Booked backlog" value={fmtMoney(d.backlog_open_jobs)} sub={`${d.coverage_3mo}× near-term coverage`} />
        <Stat label="Fresh quotes ≤90d" value={fmtMoney(d.fresh_quotes_90d)} sub={`${d.win_rate}% win → ${fmtMoney(d.fresh_expected)}`} />
        <Stat label="Leads/mo" value={d.leads_per_mo} sub={`${d.lead_to_job_pct}% → jobs · trend ${d.lead_trend}×`} />
        <Stat label="6-mo likely rev" value={fmtMoney(t.likely)} sub={`range ${fmtMoney(t.low)}–${fmtMoney(t.high)}`} />
        <Stat label="6-mo net (today's cost)" value={`${fmtMoney(t.net_likely_current_cost)}`} sub={`${t.margin_likely_current_cost}% margin`} />
      </div>
      <div style={{ ...S.cardSubtitle, marginTop: 10 }}>
        At today's cost the likely case nets <b>{t.margin_likely_current_cost}%</b>. Layer the full $127K/yr raise plan (≈−${RAISE_MO.toLocaleString()}/mo) and the likely case lands <b style={{ color: marRaises < 9 ? CHART.over : marRaises < 12 ? "#fbbf24" : CHART.net }}>{marRaises.toFixed(1)}%</b> — the conservative case dips below the 9% floor. Revenue basis: QuickBooks accrual (authoritative); jb_jobs is not used for the baseline.
      </div>

      {/* ── Marketing lever — playable ── */}
      <div style={{ marginTop: 14, padding: 14, border: "1px solid rgba(236,72,153,0.3)", borderRadius: 10, background: "rgba(236,72,153,0.05)" }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>Marketing lever — what if we spend more? <span style={{ color: CHART.axis, fontWeight: 400, fontSize: 11 }}>(directional planning, not a prediction)</span></div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 18, alignItems: "flex-end", marginTop: 10 }}>
          <label style={{ fontSize: 11, color: CHART.axis }}>Extra marketing&nbsp;$/mo
            <input type="number" min={0} max={15000} step={500} value={extraMktg}
              onChange={e => setExtraMktg(Math.max(0, Math.min(15000, Number(e.target.value) || 0)))}
              style={{ display: "block", marginTop: 4, width: 120, padding: "6px 8px", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, color: "inherit", fontSize: 14 }} />
            <span style={{ fontSize: 10 }}>on top of ~{fmtMoney(CUR_MKTG_MO)}/mo today</span>
          </label>
          <label style={{ fontSize: 11, color: CHART.axis, flex: "1 1 240px" }}>Gross revenue per $1: <b style={{ color: mult < beMult ? CHART.over : CHART.net }}>${mult.toFixed(2)}</b>
            <input type="range" min={1.5} max={4.0} step={0.25} value={mult}
              onChange={e => setMult(Number(e.target.value))}
              style={{ display: "block", marginTop: 6, width: "100%" }} />
            <span style={{ fontSize: 10 }}>break-even <b>${beMult}</b> (rev × {(cm * 100).toFixed(0)}% margin = $1) · default $2.25 sits just below, on purpose · blended-average is ~$10+ but that over-credits referrals</span>
          </label>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
          <Stat label="6-mo Δ revenue" value={hasLever ? `+${fmtMoney(sumUpRev)}` : "—"} sub={multEff < mult ? `mult capped to $${multEff.toFixed(2)} (dim. returns)` : "spend × mult × lag ramp"} />
          <Stat label="6-mo Δ net" value={hasLever ? `${sumUpNet >= 0 ? "+" : ""}${fmtMoney(sumUpNet)}` : "—"} color={hasLever ? (sumUpNet >= 0 ? CHART.net : CHART.over) : undefined} sub="Δrev × margin − the spend" />
          <Stat label="Payback" value={hasLever ? (payback ? `month ${payback}` : "not within 6 mo") : "—"} sub="cumulative Δnet turns positive" />
        </div>
        <div style={{ fontSize: 11, color: CHART.axis, marginTop: 10, lineHeight: 1.5 }}>
          <b style={{ color: "#fbbf24" }}>Read honestly:</b> at Browning's scale, extra paid marketing is roughly <b>break-even</b> — the company grew ~6× while marketing held flat at ~5% of sales, so growth is <b>referral/repeat-driven, not paid</b>. The default sits just below break-even on purpose; slide up only if you can defend a higher return for the <i>next</i> dollar (not your average). You pay from month 1 but revenue lags 2–3 months, so the in-window payback is weak. Diminishing returns are enforced past ~{fmtMoney(CUR_MKTG_MO)} extra. Spend basis: QuickBooks advertising; revenue basis: QB accrual.
        </div>
      </div>
    </div>
  );
}

// Live financial-health band — cash/runway, AR aging, client concentration.
function HealthBand() {
  const [h, setH] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    apiFetch(`${API}?action=health`)
      .then(r => r.json())
      .then(j => { if (alive) { if (j?.ok) setH(j); else setErr(j?.error || "unavailable"); } })
      .catch(() => { if (alive) setErr("unavailable"); });
    return () => { alive = false; };
  }, []);
  if (err) return null;
  if (!h) return (
    <div style={{ ...S.card, marginBottom: 16 }}>
      <div style={S.cardHeader}><div style={S.cardTitle}>Financial Health</div></div>
      <div style={S.emptyText}>Loading…</div>
    </div>
  );
  const c = h.cash || {}, ar = h.ar_aging || {}, con = h.concentration || {};
  const runwayColor = c.runway_months >= 6 ? CHART.net : c.runway_months >= 3 ? "#fbbf24" : CHART.over;
  const Tile = ({ label, value, sub, color }) => (
    <div style={{ flex: "1 1 150px", minWidth: 150, padding: "10px 12px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8 }}>
      <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, color: CHART.axis }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, marginTop: 2, color: color || "inherit" }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: CHART.axis, marginTop: 2 }}>{sub}</div>}
    </div>
  );
  const Bar = ({ label, amt, total, color }) => {
    const pct = total ? Math.round(amt / total * 100) : 0;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, margin: "4px 0" }}>
        <span style={{ width: 78, color: CHART.axis }}>{label}</span>
        <span style={{ flex: 1, height: 10, background: "rgba(255,255,255,0.05)", borderRadius: 3, overflow: "hidden" }}>
          <span style={{ display: "block", height: "100%", width: `${pct}%`, background: color, borderRadius: 3 }} />
        </span>
        <span style={{ width: 96, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmtMoney(amt)} · {pct}%</span>
      </div>
    );
  };
  return (
    <div style={{ ...S.card, marginBottom: 16 }}>
      <div style={S.cardHeader}><div style={S.cardTitle}>Financial Health — cash, receivables &amp; concentration</div></div>
      <div style={S.cardSubtitle}>Cash &amp; AP from the latest QB balance sheet ({c.bs_period}); receivables aged from open Jobber invoices; concentration from trailing-12 jobs. As of {h.as_of}.</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
        <Tile label="Cash in bank" value={fmtMoney(c.balance)} sub={`${c.runway_months} months of runway`} color={runwayColor} />
        <Tile label="Receivables (Jobber)" value={fmtMoney(ar.total)} sub={`${ar.invoice_count} open · ${ar.pct_60_plus}% over 60d`} />
        <Tile label="Owed out (AP + cards)" value={fmtMoney((c.ap || 0) + (c.credit_cards || 0))} sub={`AP ${fmtMoney(c.ap)} · cards ${fmtMoney(c.credit_cards)}`} />
        <Tile label="Client concentration" value={con.top1_pct < 15 ? "Low" : con.top1_pct < 25 ? "Moderate" : "High"} sub={`top-1 ${con.top1_pct}% · top-3 ${con.top3_pct}% · ${con.client_count} clients`} color={con.top1_pct < 15 ? CHART.net : "#fbbf24"} />
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 18, marginTop: 14 }}>
        <div style={{ flex: "1 1 320px" }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Receivables aging</div>
          <Bar label="Current" amt={ar.current} total={ar.total} color={CHART.net} />
          <Bar label="31–60d" amt={ar.d31_60} total={ar.total} color="#fbbf24" />
          <Bar label="61–90d" amt={ar.d61_90} total={ar.total} color="#fbbf24" />
          <Bar label="90+ d" amt={ar.d90_plus} total={ar.total} color={CHART.over} />
          {ar.d90_plus > 0 && <div style={{ fontSize: 10, color: CHART.over, marginTop: 4 }}>{fmtMoney(ar.d90_plus)} is 90+ days — chase before write-off{(ar.oldest_90plus || [])[0] ? ` (oldest: ${ar.oldest_90plus[0].client}, ${ar.oldest_90plus[0].days}d)` : ""}</div>}
        </div>
        <div style={{ flex: "1 1 320px" }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 6 }}>Top clients (trailing 12 mo)</div>
          {(con.top_clients || []).slice(0, 5).map((t, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "3px 0", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 200 }}>{t.client}</span>
              <span style={{ color: CHART.axis }}>{fmtMoney(t.rev)} · {t.pct}%</span>
            </div>
          ))}
          <div style={{ fontSize: 10, color: CHART.axis, marginTop: 4 }}>Well diversified — even losing the top client costs &lt;{Math.ceil(con.top1_pct || 0)}% of revenue.</div>
        </div>
      </div>
    </div>
  );
}

function SalaryCard({ salary }) {
  if (!salary) return null;
  const Row = ({ label, actualPct, targetPct, actual, budgetAmt }) => {
    const over = actualPct > targetPct;
    const max = Math.max(actualPct, targetPct, 1);
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#e8eaf0", marginBottom: 6 }}>
          <span>{label}</span>
          <span style={{ color: over ? "#fca5a5" : "#86efac" }}>
            {actualPct.toFixed(1)}% actual · {targetPct.toFixed(1)}% market
          </span>
        </div>
        <div style={{ position: "relative", height: 10, background: "rgba(255,255,255,0.06)", borderRadius: 6 }}>
          <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${(actualPct / max) * 100}%`, background: over ? "#ef4444" : "#22c55e", borderRadius: 6 }} />
          <div style={{ position: "absolute", top: -2, bottom: -2, left: `${(targetPct / max) * 100}%`, width: 2, background: "#60a5fa" }} />
        </div>
        <div style={{ fontSize: 11, color: "#8892a4", marginTop: 4 }}>
          {fmtMoney(actual)} actual · market budget {fmtMoney(budgetAmt)}
        </div>
      </div>
    );
  };
  return (
    <div style={{ ...S.card, marginBottom: 0 }}>
      <div style={S.cardHeader}><div style={S.cardTitle}>Salaries vs Market</div></div>
      <div style={S.cardSubtitle}>Blue tick = industry target. Bar turns red when over market.</div>
      <div style={{ marginTop: 14 }}>
        <Row label="Admin Wages" actualPct={salary.adminActualPct} targetPct={salary.adminTargetPct}
             actual={salary.adminActual} budgetAmt={salary.adminBudget} />
        <Row label="Field Labor" actualPct={salary.laborActualPct} targetPct={salary.laborTargetPct}
             actual={salary.laborActual} budgetAmt={salary.laborBudget} />
      </div>
    </div>
  );
}

// ── Scenario Planner panel ─────────────────────────────────────────────────
function PlannerPanel({ baseline, plan, budget, scenario, setScenario, scenarios, onLoad, onSave, onDelete, aiSummary, runAi, busy, employees, updateEmployee, saveRoster, rosterBusy, setWage, setBonus, setBurden }) {
  if (!baseline) {
    return (
      <div style={S.card}>
        <div style={S.cardHeader}><div style={S.cardTitle}>Scenario Planner</div></div>
        <div style={S.emptyText}>Backfill the fiscal year first — the planner needs live actuals to model against.</div>
      </div>
    );
  }
  const up = (patch) => setScenario(s => ({ ...s, ...patch }));
  const upPct = (b, v) => setScenario(s => ({ ...s, planned_pcts: { ...(s.planned_pcts || {}), [b]: v } }));
  const upOwner = (k, v) => setScenario(s => ({ ...s, owner: { ...(s.owner || {}), [k]: v } }));
  const addons = scenario.addons || [];
  const setAddon = (i, patch) => setScenario(s => {
    const next = [...(s.addons || [])]; next[i] = { ...next[i], ...patch }; return { ...s, addons: next };
  });
  const addAddon = () => setScenario(s => ({ ...s, addons: [...(s.addons || []), { name: "", monthly: "", sign: "+", bucket: "vehicle" }] }));
  const rmAddon = (i) => setScenario(s => ({ ...s, addons: (s.addons || []).filter((_, j) => j !== i) }));

  const p = plan;
  const netDelta = p ? p.net - p.actualNet : 0;
  const marginPill = p && p.margin >= 0.12 ? "good" : p && p.margin >= 0.09 ? "ok" : "bad";
  const ownerPill = p && p.ownerPct >= 0.08 && p.ownerPct <= 0.13 ? "good" : "bad";

  // Min revenue to hold 12% net, given this scenario's fixed overhead + CM.
  const fixedOverhead = p ? (p.overhead + p.nonOpex) : 0;
  const cm = baseline.cm || 0.39;
  const minRevFor12 = cm > 0.12 ? fixedOverhead / (cm - 0.12) : null;

  return (
    <div style={{ ...S.card, border: "1px solid rgba(245,158,11,0.35)" }}>
      <div style={S.cardHeader}>
        <div style={S.cardTitle}>Scenario Planner — what-if vs live actuals</div>
        <div style={S.cardSubtitle}>Baseline: {fmtMoney(baseline.revenue)} annualized · {baseline.monthsUsed} mo · CM {fmtPct(cm, 0)}</div>
      </div>

      {/* Scenario save/load */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        <input value={scenario.name || ""} onChange={e => up({ name: e.target.value })}
               placeholder="Scenario name (e.g. Shop + raises 2027)" style={{ ...S.input, width: 260 }} />
        <button onClick={onSave} disabled={busy || !scenario.name} style={S.btnPrimary}>{busy === "scenario" ? "Saving…" : "Save"}</button>
        <select value={scenario.id || ""} onChange={e => onLoad(e.target.value)} style={S.select}>
          <option value="">Load saved…</option>
          {scenarios.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        {scenario.id && <button onClick={onDelete} style={S.btn}>Delete</button>}
        <button onClick={() => up({ id: undefined, name: "", planned_revenue: "", planned_pcts: {}, addons: [], owner: scenario.owner })} style={S.btn}>New</button>
      </div>

      {/* Results strip */}
      <div style={S.kpiGrid}>
        <Kpi label="Planned Revenue" value={fmtMoney(p?.rev || 0)} sub={`actual ${fmtMoney(baseline.revenue)}`} />
        <Kpi label="Planned Net Profit" value={fmtMoney(p?.net || 0)}
             sub={`${netDelta >= 0 ? "+" : ""}${fmtMoney(netDelta)} vs actual`} highlight={netDelta >= 0 ? "good" : "bad"} />
        <Kpi label="Planned Net Margin" value={fmtPct(p?.margin || 0, 1)}
             sub={`target ${fmtPct(budget?.net_profit_target_pct || 0.12, 0)}`} highlight={marginPill} />
        <Kpi label="Owner Comp %" value={fmtPct(p?.ownerPct || 0, 1)}
             sub={`${fmtMoney(p?.ownerAnnual || 0)}/yr · benchmark 8–13%`} highlight={ownerPill} />
        <Kpi label="Min rev to hold 12%" value={minRevFor12 ? fmtMoney(minRevFor12) : "—"}
             sub="with this scenario's fixed costs" />
        {(p?.dividends || 0) > 0 && (
          <>
            <Kpi label="Dividends taken" value={fmtMoney(p.dividends)} sub="distribution of profit" />
            <Kpi label="Retained surplus" value={fmtMoney(p.retained)} sub="net profit − dividends"
                 highlight={p.retained >= 0 ? "good" : "bad"} />
          </>
        )}
      </div>

      {/* Planned vs Actual vs Market chart */}
      <div style={{ width: "100%", height: 300, marginTop: 8 }}>
        <ResponsiveContainer>
          <BarChart data={p?.rows || []} layout="vertical" margin={{ left: 18, right: 24, top: 4, bottom: 4 }}>
            <CartesianGrid horizontal={false} stroke={CHART.grid} />
            <XAxis type="number" tick={{ fill: CHART.axis, fontSize: 11 }} tickFormatter={v => `${v}%`} />
            <YAxis type="category" dataKey="label" width={96} tick={{ fill: CHART.axis, fontSize: 11 }} />
            <Tooltip contentStyle={S.chartTip} formatter={(v, n) => [`${v}%`, n]} />
            <Legend wrapperStyle={{ fontSize: 11, color: CHART.axis }} />
            <Bar dataKey="actual"  name="Actual"  fill={CHART.actual} radius={[0, 3, 3, 0]} barSize={8} />
            <Bar dataKey="planned" name="Planned" fill="#f59e0b"      radius={[0, 3, 3, 0]} barSize={8} />
            <Bar dataKey="target"  name="Market"  fill={CHART.target} radius={[0, 3, 3, 0]} barSize={8} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div style={{ fontSize: 11, color: "#8892a4", marginTop: 4 }}>
        <span style={{ color: CHART.actual }}>● Actual</span> (QuickBooks — incl. your splits &amp; reclass) ·
        <span style={{ color: "#f59e0b" }}> ● Planned / forecast</span> ·
        <span style={{ color: CHART.target }}> ● Market benchmark</span>
      </div>

      <div style={S.gridTwo}>
        {/* Revenue + line overrides */}
        <div>
          <div style={{ fontWeight: 700, color: "#e8eaf0", margin: "8px 0" }}>Planned levers</div>
          <label style={S.label}>Planned annual revenue</label>
          <input type="number" value={scenario.planned_revenue ?? ""} onChange={e => up({ planned_revenue: e.target.value })}
                 placeholder={String(Math.round(baseline.revenue))} style={S.input} />
          <div style={{ fontSize: 11, color: "#8892a4", marginTop: 4 }}>Blank = current run-rate. Variable costs scale automatically.</div>
          <div style={{ marginTop: 10, fontSize: 12, color: "#8892a4" }}>Override a line's % of revenue (blank = keep actual):</div>
          <div style={S.gridTwo}>
            {[...DIRECT_COST_BUCKETS, ...OVERHEAD_BUCKETS].map(b => {
              const actualPct = baseline.revenue > 0 ? (baseline.annual[b] || 0) / baseline.revenue * 100 : 0;
              return (
                <div key={b}>
                  <label style={S.label}>{BUCKET_LABEL[b]}{VARIABLE_BUCKETS.includes(b) ? " (var)" : ""}</label>
                  <input type="number" step="0.1" value={scenario.planned_pcts?.[b] ?? ""} onChange={e => upPct(b, e.target.value)}
                         placeholder={actualPct.toFixed(1)} style={S.input} />
                </div>
              );
            })}
          </div>
        </div>

        {/* Adjustments / add-ons */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "8px 0" }}>
            <div style={{ fontWeight: 700, color: "#e8eaf0" }}>Adjustments (new van, shop, raises…)</div>
            <button onClick={addAddon} style={S.btn}>+ Add</button>
          </div>
          {addons.length === 0 && <div style={S.emptyText}>No adjustments. Add a recurring monthly cost and tag its QB category — e.g. a new admin hire tagged Admin Wages (separate from bumping owner payroll above), a van tagged Vehicle, or a shop tagged Other Indirects.</div>}
          {addons.map((a, i) => (
            <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center" }}>
              <input value={a.name} onChange={e => setAddon(i, { name: e.target.value })} placeholder="Name" style={{ ...S.input, flex: 2 }} />
              <select value={a.sign} onChange={e => setAddon(i, { sign: e.target.value })} style={{ ...S.select, width: 52 }}>
                <option value="+">+</option><option value="-">−</option>
              </select>
              <input type="number" value={a.monthly} onChange={e => setAddon(i, { monthly: e.target.value })} placeholder="$/mo" style={{ ...S.input, flex: 1 }} />
              <select value={a.bucket} onChange={e => setAddon(i, { bucket: e.target.value })} style={{ ...S.select, flex: 1 }}>
                {BUCKET_OPTIONS.map(b => <option key={b} value={b}>{BUCKET_LABEL[b]}</option>)}
              </select>
              <button onClick={() => rmAddon(i)} style={S.iconBtn}>×</button>
            </div>
          ))}
          <div style={{ marginTop: 14, fontWeight: 700, color: "#e8eaf0" }}>Owner &amp; staff pay</div>
          <div style={{ fontSize: 11, color: "#8892a4", marginTop: 4 }}>
            Set everyone's rate in the <b>Employee roster</b> below — owners included. Owner raises land in Owner Wages
            (Colton splits {p?.roster?.coltonOfficePct ?? 25}% office / rest Direct Labour). Current owner payroll change from your edits:
            <b style={{ color: "#e8eaf0" }}> {p && p.ownerDelta ? (p.ownerDelta >= 0 ? "+" : "") + fmtMoney(p.ownerDelta) : "$0"}/yr</b>.
          </div>

          {/* Owner salaries vs market (owners-only, separate from total Admin Wages) */}
          <div style={{ marginTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#e8eaf0", marginBottom: 6 }}>
              <span>Owner salaries vs market</span>
              <span style={{ color: (p?.ownerPct || 0) > 0.13 ? "#fca5a5" : "#86efac" }}>{fmtPct(p?.ownerPct || 0, 1)} · market 8–13%</span>
            </div>
            <div style={{ position: "relative", height: 10, background: "rgba(255,255,255,0.06)", borderRadius: 6 }}>
              <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${Math.min(100, (p?.ownerPct || 0) / 0.16 * 100)}%`, background: (p?.ownerPct || 0) > 0.13 ? "#ef4444" : "#22c55e", borderRadius: 6 }} />
              <div style={{ position: "absolute", top: -2, bottom: -2, left: `${0.08 / 0.16 * 100}%`, width: 2, background: "#60a5fa" }} />
              <div style={{ position: "absolute", top: -2, bottom: -2, left: `${0.13 / 0.16 * 100}%`, width: 2, background: "#60a5fa" }} />
            </div>
            <div style={{ fontSize: 11, color: "#8892a4", marginTop: 4 }}>
              {fmtMoney(p?.ownerAnnual || 0)}/yr total owner comp (Brownings + Colton, incl. Colton's billable in Direct Labour).
              Office portion shows in the <b style={{ color: "#e8eaf0" }}>Owner Wages</b> bucket ({fmtMoney(p?.ownerWagesPlanned || 0)}); Admin Wages is staff-only.
              Tag owner pay accounts (and Colton's office split) to Owner Wages in <b style={{ color: "#e8eaf0" }}>Account Mapping</b> so the actuals match.
            </div>
          </div>

          <div style={{ marginTop: 12, fontWeight: 700, color: "#e8eaf0" }}>Dividends taken (annual)</div>
          <div style={S.gridTwo}>
            <div>
              <label style={S.label}>Brownings dividends</label>
              <input type="number" value={scenario.owner?.brownings_dividend ?? ""} onChange={e => upOwner("brownings_dividend", e.target.value)} style={S.input} />
            </div>
            <div>
              <label style={S.label}>Colton dividends</label>
              <input type="number" value={scenario.owner?.colton_dividend ?? ""} onChange={e => upOwner("colton_dividend", e.target.value)} style={S.input} />
            </div>
          </div>
          <div style={{ fontSize: 11, color: "#8892a4", marginTop: 4 }}>
            Paid from after-tax profit — doesn't change net margin. Retained after dividends:{" "}
            <b style={{ color: "#e8eaf0" }}>{fmtMoney(p?.retained || 0)}</b> · total owner extraction {fmtPct(p?.extractionPct || 0, 1)} of revenue.
          </div>
        </div>
      </div>

      {/* Employee roster — standard vs planned rates */}
      <EmployeeRoster employees={employees} updateEmployee={updateEmployee} saveRoster={saveRoster}
        rosterBusy={rosterBusy} wages={scenario.wages} setWage={setWage}
        bonuses={scenario.bonuses} setBonus={setBonus} burdenPct={scenario.burden_pct} setBurden={setBurden}
        roster={p?.roster} />

      {/* Tax calculator */}
      <TaxCalc scenario={scenario} setScenario={setScenario} roster={p?.roster} />

      {/* AI summary */}
      <div style={{ marginTop: 16, borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 14 }}>
        <button onClick={runAi} disabled={busy === "ai"} style={S.btnPrimary}>
          {busy === "ai" ? "Asking Jai…" : "Generate AI summary"}
        </button>
        {aiSummary && (
          <div style={{ marginTop: 12, background: "rgba(165,206,58,0.05)", border: "1px solid rgba(165,206,58,0.2)", borderRadius: 10, padding: 14 }}>
            <div style={{ fontWeight: 700, color: "#e8eaf0", marginBottom: 6 }}>{aiSummary.headline}</div>
            {(aiSummary.risks || []).length > 0 && (
              <ul style={{ margin: "6px 0", paddingLeft: 18, color: "#fca5a5", fontSize: 13 }}>
                {aiSummary.risks.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            )}
            <div style={{ fontSize: 13, color: "#c9d2de", marginTop: 6 }}>{aiSummary.recommendation}</div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Employee roster — standard vs planned rates ────────────────────────────
function EmployeeRoster({ employees, updateEmployee, saveRoster, rosterBusy, wages, setWage, bonuses, setBonus, burdenPct, setBurden, roster }) {
  const byName = {};
  (roster?.perEmployee || []).forEach(pe => { byName[pe.name] = pe; });
  const w = wages || {}; const bz = bonuses || {};
  return (
    <div style={{ marginTop: 16, borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
        <div style={{ fontWeight: 700, color: "#e8eaf0" }}>Employee roster — standard rate vs planned</div>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <label style={{ fontSize: 11, color: "#8892a4" }}>Burden on raises %</label>
          <input type="number" value={burdenPct ?? ""} placeholder="3" onChange={e => setBurden(e.target.value)} style={{ ...S.input, width: 64, textAlign: "right" }} />
          <button onClick={saveRoster} disabled={rosterBusy} style={S.btn}>{rosterBusy ? "Saving…" : "Save standard rates"}</button>
        </div>
      </div>
      <div style={{ fontSize: 11, color: "#8892a4", marginBottom: 8 }}>
        Standard rate = today (no change). Planned rate &amp; bonus = the what-if (saved with the scenario). Hours come from
        Jobber timesheets — edit "TS name" if hours read 0 (name mismatch). Salary rows are $/mo; hourly are $/hr.
        <b> Burden %</b> adds CPP/EI/WCB only on the <i>increase</i> (baseline burden is already in QuickBooks); default 3%
        because most full-timers are over the CPP/EI cap.
      </div>
      <div style={S.tableScroll}>
        <table style={S.bsTable}>
          <thead><tr>
            <th style={{ ...S.th, textAlign: "left" }}>Employee</th>
            <th style={{ ...S.th, textAlign: "left" }}>TS name</th>
            <th style={{ ...S.th, textAlign: "left" }}>Bucket</th>
            <th style={S.th}>Office %</th>
            <th style={S.th}>Std rate</th>
            <th style={S.th}>Hours/yr</th>
            <th style={S.th}>Planned rate</th>
            <th style={S.th}>Bonus $/mo</th>
            <th style={S.th}>Δ /yr (loaded)</th>
          </tr></thead>
          <tbody>
            {(employees || []).map(e => {
              const pe = byName[e.employee_name] || {};
              const salary = e.pay_type === "salary";
              const d = pe.delta || 0;
              const noHours = !salary && (e.actual_hours == null || e.actual_hours === 0) && (e.hours_override == null || e.hours_override === "");
              return (
                <tr key={e.employee_name}>
                  <td style={S.tdLabel}>{e.employee_name}{salary ? " · salary" : ""}</td>
                  <td>
                    <input value={e.first_name ?? ""} onChange={ev => updateEmployee(e.employee_name, { first_name: ev.target.value })}
                           style={{ ...S.input, width: 72, ...(noHours ? { borderColor: "#c98a16" } : {}) }} />
                  </td>
                  <td>
                    <select value={e.bucket} onChange={ev => updateEmployee(e.employee_name, { bucket: ev.target.value })} style={{ ...S.select, fontSize: 11 }}>
                      {["owner_wages", "labor", "admin_wages"].map(b => <option key={b} value={b}>{BUCKET_LABEL[b]}</option>)}
                    </select>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {e.bucket === "owner_wages" ? (
                      <input type="number" value={e.office_pct ?? ""} placeholder="100"
                             onChange={ev => updateEmployee(e.employee_name, { office_pct: ev.target.value })} style={{ ...S.input, width: 56, textAlign: "right" }} />
                    ) : <span style={{ color: "#5e6675" }}>—</span>}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <input type="number" value={e.rate ?? ""} onChange={ev => updateEmployee(e.employee_name, { rate: ev.target.value })} style={{ ...S.input, width: 72, textAlign: "right" }} />
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {salary ? <span style={{ color: "#5e6675" }}>—</span> :
                      <input type="number" value={e.hours_override ?? ""} placeholder={e.actual_hours != null ? String(e.actual_hours) : "2080"}
                             onChange={ev => updateEmployee(e.employee_name, { hours_override: ev.target.value })} style={{ ...S.input, width: 76, textAlign: "right" }} />}
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <input type="number" value={w[e.employee_name] ?? ""} placeholder={String(e.rate ?? "")}
                           onChange={ev => setWage(e.employee_name, ev.target.value)} style={{ ...S.input, width: 72, textAlign: "right" }} />
                  </td>
                  <td style={{ textAlign: "right" }}>
                    <input type="number" value={bz[e.employee_name] ?? ""} placeholder="0"
                           onChange={ev => setBonus(e.employee_name, ev.target.value)} style={{ ...S.input, width: 70, textAlign: "right" }} />
                  </td>
                  <td style={{ ...S.td, color: d > 0 ? "#fca5a5" : d < 0 ? "#86efac" : "#5e6675", fontWeight: 700 }}>
                    {d ? (d > 0 ? "+" : "") + fmtMoney(d) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {roster && (
        <div style={{ fontSize: 12, color: "#8892a4", marginTop: 8 }}>
          Wage bill: <b style={{ color: "#e8eaf0" }}>{fmtMoney(roster.totalStdAnnual)}</b> →{" "}
          <b style={{ color: "#e8eaf0" }}>{fmtMoney(roster.totalPlanAnnual)}</b>{" "}
          ({(roster.totalPlanAnnual - roster.totalStdAnnual) >= 0 ? "+" : ""}{fmtMoney(roster.totalPlanAnnual - roster.totalStdAnnual)}/yr, pre-burden).
          Δ/yr is loaded (incl. {(burdenPct != null && burdenPct !== "") ? burdenPct : 3}% burden + bonus). Amber TS-name = no timesheet hours matched.
        </div>
      )}
    </div>
  );
}

// ── BC + Federal salary tax calculator (income-splitting planner) ──────────
function TaxCalc({ scenario, setScenario, roster }) {
  const tax = scenario.tax || {};
  const targetNet = Number(tax.target_net) || 0;
  const allieShare = tax.allie_share == null ? 0.5 : Number(tax.allie_share);
  const upTax = (patch) => setScenario(s => ({ ...s, tax: { ...(s.tax || {}), ...patch } }));

  const single = targetNet > 0 ? grossForNet(targetNet) : 0;
  const splitDylan = targetNet > 0 ? grossForNet(targetNet * (1 - allieShare)) : 0;
  const splitAllie = targetNet > 0 ? grossForNet(targetNet * allieShare) : 0;
  const splitGross = splitDylan + splitAllie;
  const saved = single - splitGross;

  // Dividends taken (from the Owners panel), taxed marginally on top of the
  // salary each of Dylan/Allie draws, split by allieShare. Proper 2025
  // non-eligible-dividend gross-up + DTC math.
  const browningsDiv = Number(scenario.owner?.brownings_dividend) || 0;
  const browningsSalary = (roster?.browningsPlanMo || 0) * 12;
  const dylanSalary = browningsSalary * (1 - allieShare);
  const allieSalary = browningsSalary * allieShare;
  const dDiv = dividendTax(browningsDiv * (1 - allieShare), dylanSalary);
  const aDiv = dividendTax(browningsDiv * allieShare, allieSalary);
  const divTaxTotal = dDiv.tax + aDiv.tax;
  const netDiv = browningsDiv - divTaxTotal;
  const effDivRate = browningsDiv > 0 ? divTaxTotal / browningsDiv : 0;
  // Actual salary net (from the salary drawn), for the combined take-home line.
  const salaryNetActual = (dylanSalary - incomeTax(dylanSalary).total) + (allieSalary - incomeTax(allieSalary).total);
  const combined = salaryNetActual + netDiv;

  return (
    <div style={{ marginTop: 16, borderTop: "1px solid rgba(255,255,255,0.06)", paddingTop: 14 }}>
      <div style={{ fontWeight: 700, color: "#e8eaf0", marginBottom: 4 }}>Brownings gross/net & income-splitting (BC + Federal, 2025)</div>
      <div style={S.cardSubtitle}>How much gross the household must draw for a target take-home — single earner vs split between Dylan &amp; Allie.</div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end", marginTop: 10 }}>
        <div>
          <label style={S.label}>Target household take-home ($/yr)</label>
          <input type="number" value={tax.target_net ?? ""} onChange={e => upTax({ target_net: e.target.value })} placeholder="120000" style={{ ...S.input, width: 200 }} />
        </div>
        <div>
          <label style={S.label}>Allie's share of Brownings income: {(allieShare * 100).toFixed(0)}%</label>
          <input type="range" min="0" max="0.5" step="0.05" value={allieShare} onChange={e => upTax({ allie_share: Number(e.target.value) })} style={{ width: 180 }} />
        </div>
      </div>
      {targetNet > 0 && (
        <div style={{ ...S.kpiGrid, marginTop: 12 }}>
          <Kpi label="Single-earner gross" value={fmtMoney(single)} sub={`tax ${fmtMoney(single - targetNet)}`} />
          <Kpi label="Split gross (combined)" value={fmtMoney(splitGross)}
               sub={`Dylan ${fmtMoney(splitDylan)} · Allie ${fmtMoney(splitAllie)}`} />
          <Kpi label="Saved by splitting" value={fmtMoney(saved)} sub="lower gross for same take-home" highlight={saved > 0 ? "good" : undefined} />
        </div>
      )}
      {browningsDiv > 0 && (
        <div style={{ ...S.kpiGrid, marginTop: 12 }}>
          <Kpi label="Dividends (gross)" value={fmtMoney(browningsDiv)} sub="non-eligible / small-business" />
          <Kpi label="Est. dividend tax" value={fmtMoney(divTaxTotal)}
               sub={`${fmtPct(effDivRate, 1)} effective · stacked on salary`} />
          <Kpi label="Net dividends" value={fmtMoney(netDiv)} />
          <Kpi label="Combined take-home" value={fmtMoney(combined)} sub="salary net + net dividends" highlight="good" />
        </div>
      )}
      <div style={{ fontSize: 11, color: "#5e6675", marginTop: 8 }}>
        Estimates only — 2025 BC+Federal salary brackets and non-eligible (small-business) dividend gross-up + dividend tax credit, taxed marginally on top of salary. Excludes RRSP, eligible dividends, and credit nuances. Confirm with your accountant before planning draws.
      </div>
    </div>
  );
}

// ── Printable report (self-contained HTML, light theme) ────────────────────
function reportKpiBox(label, value, sub, good) {
  const color = good === undefined ? "#0f1b2d" : (good ? "#1f9d6b" : "#d04848");
  return `<div style="flex:1;min-width:130px;border:1px solid #e3e8f0;border-radius:12px;padding:14px">
    <div style="font-size:11px;text-transform:uppercase;letter-spacing:.5px;color:#5b6b7a">${label}</div>
    <div style="font-size:22px;font-weight:800;color:${color};margin-top:4px">${value}</div>
    ${sub ? `<div style="font-size:11px;color:#8893a5;margin-top:2px">${sub}</div>` : ""}
  </div>`;
}
function buildReportHtml(c, opts) {
  const rows = c.benchmarkData.map(d => {
    const bad = d.isCost ? d.actual > d.target : d.actual < d.target;
    const color = bad ? "#d04848" : "#1f9d6b";
    const denom = Math.max(d.actual, d.target, 1);
    const w = Math.min(100, (d.actual / denom) * 100);
    const tw = Math.min(100, (d.target / denom) * 100);
    return `<tr>
      <td style="padding:8px 10px;border-bottom:1px solid #eee">${d.label}</td>
      <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:right;font-variant-numeric:tabular-nums">${fmtMoney(d.amount)}</td>
      <td style="padding:8px 10px;border-bottom:1px solid #eee;width:40%">
        <div style="position:relative;height:12px;background:#f0f2f6;border-radius:6px">
          <div style="position:absolute;left:0;top:0;bottom:0;width:${w}%;background:${color};border-radius:6px"></div>
          <div style="position:absolute;top:-2px;bottom:-2px;left:${tw}%;width:2px;background:#2f5fd0"></div>
        </div>
      </td>
      <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:right;color:${color};font-weight:700">${d.actual}%</td>
      <td style="padding:8px 10px;border-bottom:1px solid #eee;text-align:right;color:#666">${d.target}%</td>
    </tr>`;
  }).join("");
  const netMargin = c.monthRevenue > 0 ? (c.monthNet / c.monthRevenue) * 100 : 0;
  const coverage = c.missingMonths?.length
    ? `<div style="margin-top:10px;font-size:12px;color:#c98a16">Coverage: ${c.effectiveCount} month(s) included. Missing: ${c.missingMonths.map(periodLabel).join(", ")}.</div>` : "";
  return `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Browning Electric — P&L vs Market — ${opts.label}</title>
<style>@media print{.noprint{display:none}} body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;color:#0f1b2d;margin:0;padding:32px;background:#fff;max-width:900px}</style>
</head><body>
<div style="display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #16294a;padding-bottom:12px">
  <div><div style="font-size:22px;font-weight:800">Browning Electric — P&L vs Market</div>
  <div style="color:#5b6b7a;font-size:13px">${opts.viewLabel} · ${opts.label}</div></div>
  <div style="text-align:right;color:#5b6b7a;font-size:12px">Generated ${opts.generatedAt}<br/>Live from QuickBooks</div>
</div>
<div style="display:flex;gap:14px;margin:18px 0;flex-wrap:wrap">
  ${reportKpiBox("Revenue", fmtMoney(c.monthRevenue))}
  ${reportKpiBox("Net Profit", fmtMoney(c.monthNet))}
  ${reportKpiBox("Net Margin", netMargin.toFixed(1) + "%", "target " + opts.netTargetPct.toFixed(0) + "%", netMargin >= opts.netTargetPct)}
  ${reportKpiBox("Gross Profit", fmtMoney(c.monthGP))}
</div>
<h3 style="font-size:13px;text-transform:uppercase;letter-spacing:1px;color:#5b6b7a">Cost structure vs market</h3>
<table style="width:100%;border-collapse:collapse;font-size:13px">
  <thead><tr style="text-align:left;color:#5b6b7a;font-size:11px;text-transform:uppercase">
    <th style="padding:8px 10px">Line</th><th style="padding:8px 10px;text-align:right">Amount</th>
    <th style="padding:8px 10px">Actual vs market</th><th style="padding:8px 10px;text-align:right">Actual %</th>
    <th style="padding:8px 10px;text-align:right">Market %</th></tr></thead>
  <tbody>${rows}</tbody>
</table>
<div style="margin-top:8px;font-size:11px;color:#8893a5">Blue tick = industry target. Bar green at/under target (cost lines) / at-or-over (net profit), red otherwise.</div>
${coverage}
<h3 style="font-size:13px;text-transform:uppercase;letter-spacing:1px;color:#5b6b7a;margin-top:24px">Balance sheet &amp; liquidity</h3>
<div style="display:flex;gap:14px;flex-wrap:wrap">
  ${reportKpiBox("Cash", fmtMoney(c.bs.cash))}
  ${reportKpiBox("AR (" + c.arSource + ")", fmtMoney(c.arNow))}
  ${reportKpiBox("AP + CC + LOC", fmtMoney(c.quickLiab))}
  ${reportKpiBox("Quick Ratio", c.quickRatio != null ? c.quickRatio.toFixed(2) : "—", "target ≥ 3.0", (c.quickRatio || 0) >= 3)}
</div>
<div class="noprint" style="margin-top:28px;text-align:center">
  <button onclick="window.print()" style="padding:10px 22px;background:#16294a;color:#fff;border:0;border-radius:8px;font-size:14px;cursor:pointer">Print / Save as PDF</button>
</div>
</body></html>`;
}

// ── Partner report: Jai-style summary from live actuals + the active scenario ─
function buildPartnerReport(baseline, plan, scenario, budget, opts) {
  const lp = (budget && budget.line_pcts) || {};
  const rev = baseline.revenue;
  const planRev = plan ? plan.rev : rev;
  const netFloor = (budget && budget.net_profit_target_pct) || 0.12;
  const buckets = [
    ["labor", "Direct Labour"], ["material", "Material"], ["subcontractors", "Subcontractors"],
    ["other_directs", "Other Directs"], ["owner_wages", "Owner Wages"], ["admin_wages", "Admin Wages (staff)"],
    ["marketing", "Marketing"], ["vehicle", "Vehicle"], ["other_indirects", "Other Indirects"],
  ];
  const cL = "padding:7px 10px;border-bottom:1px solid #eee";
  const cR = "padding:7px 10px;border-bottom:1px solid #eee;text-align:right;font-variant-numeric:tabular-nums";
  const th = "padding:7px 10px;text-align:left;color:#5b6b7a;font-size:11px;text-transform:uppercase";
  const thR = "padding:7px 10px;text-align:right;color:#5b6b7a;font-size:11px;text-transform:uppercase";

  const s1rows = buckets.map(([k, label]) => {
    const amt = baseline.annual[k] || 0;
    const p = rev > 0 ? amt / rev * 100 : 0;
    const t = (lp[k] || 0) * 100;
    const over = t > 0 && p > t;
    const status = t > 0 ? (over ? "⚠️ Over market" : "✅ Under market") : "—";
    const color = t > 0 ? (over ? "#d04848" : "#1f9d6b") : "#5b6b7a";
    return `<tr><td style="${cL}">${label}</td><td style="${cR}">${fmtMoney(amt)}</td><td style="${cR}">${p.toFixed(1)}%</td><td style="${cR}">${t ? t.toFixed(0) + "%" : "—"}</td><td style="${cL};color:${color};font-weight:600">${status}</td></tr>`;
  }).join("");
  const totalCosts = rev - baseline.net;
  const actualMargin = rev > 0 ? baseline.net / rev : 0;

  // Owner comp (proposed) — from the roster when present, else legacy fields.
  const rosterR = plan && plan.roster;
  const ow = (scenario && scenario.owner) || {};
  const numv = (v, d) => (v != null && v !== "" ? Number(v) : d);
  const bMo = rosterR ? rosterR.browningsPlanMo : (Number(ow.brownings_monthly) || 0);
  const cMo = rosterR ? rosterR.coltonPlanMo : (Number(ow.colton_monthly) || 0);
  const ownerProposed = rosterR ? rosterR.ownerPlanAnnual : (bMo + cMo) * 12;
  const ownerCurrent = rosterR ? rosterR.ownerStdAnnual : (numv(ow.brownings_current_mo, bMo) + numv(ow.colton_current_mo, cMo)) * 12;
  const ownerDelta = plan ? plan.ownerDelta : (ownerProposed - ownerCurrent);
  const hasOwner = ownerProposed > 0;
  const addons = (scenario && scenario.addons) || [];
  const addonAnnual = addons.reduce((s, a) => s + (Number(a.monthly) || 0) * 12 * (a.sign === "-" ? -1 : 1), 0);
  const hasChanges = plan && (Math.abs(ownerDelta) > 0 || addons.some(a => Number(a.monthly)) || planRev !== rev);

  const ownerSection = hasOwner ? `
  <h3 style="font-size:13px;text-transform:uppercase;letter-spacing:1px;color:#5b6b7a;margin-top:26px">Owner compensation vs market</h3>
  <table style="width:100%;border-collapse:collapse;font-size:13px">
    <thead><tr><th style="${th}">Owner</th><th style="${thR}">Monthly</th><th style="${thR}">Annual</th><th style="${thR}">% of revenue</th></tr></thead>
    <tbody>
      <tr><td style="${cL}">Brownings (Dylan + Allie)</td><td style="${cR}">${fmtMoney(bMo)}</td><td style="${cR}">${fmtMoney(bMo * 12)}</td><td style="${cR}">${planRev > 0 ? (bMo * 12 / planRev * 100).toFixed(1) : "0"}%</td></tr>
      <tr><td style="${cL}">Colton</td><td style="${cR}">${fmtMoney(cMo)}</td><td style="${cR}">${fmtMoney(cMo * 12)}</td><td style="${cR}">${planRev > 0 ? (cMo * 12 / planRev * 100).toFixed(1) : "0"}%</td></tr>
      <tr style="font-weight:800;background:#fafbfe"><td style="${cL}">Combined owner comp</td><td style="${cR}">${fmtMoney(bMo + cMo)}</td><td style="${cR}">${fmtMoney(ownerProposed)}</td><td style="${cR}">${planRev > 0 ? (ownerProposed / planRev * 100).toFixed(1) : "0"}%</td></tr>
    </tbody>
  </table>
  <div style="margin-top:6px;font-size:12px;color:${(plan && plan.ownerPct >= 0.08 && plan.ownerPct <= 0.13) ? "#1f9d6b" : "#c98a16"}">
    Industry range for two working owners ($2–5M shop): <b>8–13%</b> of revenue — Browning at ${planRev > 0 ? (ownerProposed / planRev * 100).toFixed(1) : "0"}% ${(plan && plan.ownerPct >= 0.08 && plan.ownerPct <= 0.13) ? "✅ in range" : "⚠️ review"}.
  </div>` : "";

  const changeRows = [];
  if (Math.abs(ownerDelta) > 0.5) changeRows.push(["Owner salary change", ownerDelta / 12, ownerDelta]);
  for (const a of addons) {
    const mo = (Number(a.monthly) || 0) * (a.sign === "-" ? -1 : 1);
    if (mo) changeRows.push([(a.name || "Adjustment") + " — " + (BUCKET_LABEL[a.bucket] || a.bucket), mo, mo * 12]);
  }
  const netNew = ownerDelta + addonAnnual;
  const changesSection = (hasChanges && changeRows.length) ? `
  <h3 style="font-size:13px;text-transform:uppercase;letter-spacing:1px;color:#5b6b7a;margin-top:26px">Proposed changes</h3>
  <table style="width:100%;border-collapse:collapse;font-size:13px">
    <thead><tr><th style="${th}">Change</th><th style="${thR}">Monthly</th><th style="${thR}">Annual</th></tr></thead>
    <tbody>
      ${changeRows.map(([n, mo, an]) => `<tr><td style="${cL}">${n}</td><td style="${cR}">${(mo >= 0 ? "+" : "") + fmtMoney(mo)}</td><td style="${cR}">${(an >= 0 ? "+" : "") + fmtMoney(an)}</td></tr>`).join("")}
      <tr style="font-weight:800;background:#fafbfe"><td style="${cL}">Net new cost / year</td><td style="${cR}">${(netNew >= 0 ? "+" : "") + fmtMoney(netNew / 12)}</td><td style="${cR}">${(netNew >= 0 ? "+" : "") + fmtMoney(netNew)}</td></tr>
    </tbody>
  </table>` : "";

  const cmp = (label, a, b, fmt) => `<tr><td style="${cL}">${label}</td><td style="${cR}">${fmt(a)}</td><td style="${cR}">${fmt(b)}</td></tr>`;
  const compareSection = (plan && hasChanges) ? `
  <h3 style="font-size:13px;text-transform:uppercase;letter-spacing:1px;color:#5b6b7a;margin-top:26px">Current vs proposed</h3>
  <table style="width:100%;border-collapse:collapse;font-size:13px">
    <thead><tr><th style="${th}">Measure</th><th style="${thR}">Current (actual)</th><th style="${thR}">Proposed</th></tr></thead>
    <tbody>
      ${cmp("Revenue", rev, planRev, fmtMoney)}
      ${cmp("Total costs", totalCosts, planRev - plan.net, fmtMoney)}
      <tr style="font-weight:800;background:#fafbfe"><td style="${cL}">Net profit</td><td style="${cR}">${fmtMoney(baseline.net)}</td><td style="${cR};color:${plan.net >= baseline.net ? "#1f9d6b" : "#d04848"}">${fmtMoney(plan.net)}</td></tr>
      <tr><td style="${cL}">Net margin</td><td style="${cR}">${(actualMargin * 100).toFixed(1)}%</td><td style="${cR};color:${plan.margin >= netFloor ? "#1f9d6b" : "#d04848"}">${(plan.margin * 100).toFixed(1)}%</td></tr>
      <tr><td style="${cL}">Owner comp % of revenue</td><td style="${cR}">${rev > 0 ? (ownerCurrent / rev * 100).toFixed(1) : "0"}%</td><td style="${cR}">${(plan.ownerPct * 100).toFixed(1)}%</td></tr>
    </tbody>
  </table>
  <div style="margin-top:8px;font-size:12px;color:#5b6b7a">
    Proposed net margin ${(plan.margin * 100).toFixed(1)}% vs ${(netFloor * 100).toFixed(0)}% floor — ${plan.margin >= netFloor ? "✅ clears it" : "⚠️ below floor"}.
    ${planRev === rev ? "Holds revenue flat; variable costs unchanged." : "Variable costs scale with the planned revenue at the live contribution margin."}
  </div>` : "";

  const coverage = baseline.monthsUsed < 12
    ? `<div style="margin-top:6px;font-size:12px;color:#c98a16">Note: ${baseline.monthsUsed} month(s) of data annualized — backfill the full fiscal year for exact actuals.</div>` : "";

  return `<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>Browning Electric — Partner Financial Summary — ${opts.fyLabel}</title>
<style>@media print{.noprint{display:none}} body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Arial,sans-serif;color:#0f1b2d;margin:0 auto;padding:34px;background:#fff;max-width:880px}</style>
</head><body>
<div style="display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #16294a;padding-bottom:12px">
  <div><div style="font-size:23px;font-weight:800">Browning Electric — Partner Financial Summary</div>
  <div style="color:#5b6b7a;font-size:13px">${opts.fyLabel} · For Dylan &amp; Colton</div></div>
  <div style="text-align:right;color:#5b6b7a;font-size:12px">Generated ${opts.generatedAt}<br/>Live from QuickBooks</div>
</div>

<div style="display:flex;gap:14px;margin:18px 0;flex-wrap:wrap">
  ${reportKpiBox("Revenue", fmtMoney(rev))}
  ${reportKpiBox("Net Profit", fmtMoney(baseline.net))}
  ${reportKpiBox("Net Margin", (actualMargin * 100).toFixed(1) + "%", "floor " + (netFloor * 100).toFixed(0) + "%", actualMargin >= netFloor)}
  ${reportKpiBox("Contribution Margin", (baseline.cm * 100).toFixed(0) + "%", "variable-cost basis")}
</div>

<h3 style="font-size:13px;text-transform:uppercase;letter-spacing:1px;color:#5b6b7a">Where we stand today (actual)</h3>
<table style="width:100%;border-collapse:collapse;font-size:13px">
  <thead><tr><th style="${th}">Bucket</th><th style="${thR}">Annual</th><th style="${thR}">% of revenue</th><th style="${thR}">Industry target</th><th style="${th}">Status</th></tr></thead>
  <tbody>
    ${s1rows}
    <tr style="font-weight:700"><td style="${cL}">Total costs</td><td style="${cR}">${fmtMoney(totalCosts)}</td><td style="${cR}">${rev > 0 ? (totalCosts / rev * 100).toFixed(1) : "0"}%</td><td style="${cR}">—</td><td style="${cL}"></td></tr>
    <tr style="font-weight:800;background:#fafbfe"><td style="${cL}">Net profit</td><td style="${cR}">${fmtMoney(baseline.net)}</td><td style="${cR}">${(actualMargin * 100).toFixed(1)}%</td><td style="${cR}">${(netFloor * 100).toFixed(0)}% floor</td><td style="${cL};color:#1f9d6b;font-weight:600">${actualMargin >= netFloor ? "✅ Beating it" : "⚠️ Below"}</td></tr>
  </tbody>
</table>
${coverage}
<div style="margin-top:10px;font-size:12px;color:#5b6b7a;border-left:3px solid #1f9d6b;padding-left:10px">
  Net profit is what remains <b>after every salary</b> — Dylan, Allie, Colton, Taylor — is paid in full. Owner wages are already a business expense; this is genuine retained surplus.
</div>

${ownerSection}
${changesSection}
${compareSection}

<div style="margin-top:24px;font-size:11px;color:#8893a5">
  Figures pulled live from QuickBooks (fiscal year ending May). Variable costs (labour, material, subs, other directs) scale with revenue at the contribution margin; overhead is held fixed. Owner-comp benchmark 8–13%; admin-wages benchmark 16%. Estimates for planning — confirm with your accountant.
</div>
<div class="noprint" style="margin-top:24px;text-align:center">
  <button onclick="window.print()" style="padding:10px 22px;background:#16294a;color:#fff;border:0;border-radius:8px;font-size:14px;cursor:pointer">Print / Save as PDF</button>
</div>
</body></html>`;
}

// ── Variance table ────────────────────────────────────────────────────────
function VarianceTable({ title, ytdTitle, computed }) {
  const c = computed;
  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <div style={S.cardTitle}>P&L Variance — Month vs YTD</div>
      </div>
      <div style={S.tableScroll}>
        <table style={S.varTable}>
          <thead>
            <tr>
              <th style={{ ...S.th, textAlign: "left", minWidth: 140 }}></th>
              <th colSpan={5} style={{ ...S.thGroup, background: "rgba(165,206,58,0.06)" }}>{title}</th>
              <th colSpan={5} style={{ ...S.thGroup, background: "rgba(96,165,250,0.06)" }}>{ytdTitle}</th>
            </tr>
            <tr>
              <th style={S.th}></th>
              <th style={S.th}>Actual</th>
              <th style={S.th}>%</th>
              <th style={S.th}>Budget</th>
              <th style={S.th}>%</th>
              <th style={S.thVar}>Var %</th>
              <th style={S.th}>Actual</th>
              <th style={S.th}>%</th>
              <th style={S.th}>Budget</th>
              <th style={S.th}>%</th>
              <th style={S.thVar}>Var %</th>
            </tr>
          </thead>
          <tbody>
            <VarRow label="Revenue" highlight
              monthActual={c.monthRevenue} monthRev={c.monthRevenue} monthBudget={c.monthBudgetRevenue}
              ytdActual={c.ytdRevenue} ytdRev={c.ytdRevenue} ytdBudget={c.ytdBudgetRevenue}
              isCost={false}
            />
            <SectionHeader label="Direct Cost" />
            {c.directRows.map(r => (
              <VarRow key={r.bucket} label={BUCKET_LABEL[r.bucket]}
                monthActual={r.month.actual} monthRev={c.monthRevenue} monthBudget={r.month.budgetAmt}
                ytdActual={r.ytd.actual}     ytdRev={c.ytdRevenue}     ytdBudget={r.ytd.budgetAmt}
                isCost
              />
            ))}
            <VarRow label="Total Direct Cost" subtotal
              monthActual={c.monthDirect} monthRev={c.monthRevenue} monthBudget={c.monthDirectBudget}
              ytdActual={c.ytdDirect}     ytdRev={c.ytdRevenue}     ytdBudget={c.ytdDirectBudget}
              isCost
            />
            <VarRow label="Gross Profit" highlight
              monthActual={c.monthGP} monthRev={c.monthRevenue} monthBudget={c.monthGpBudget}
              ytdActual={c.ytdGP}     ytdRev={c.ytdRevenue}     ytdBudget={c.ytdGpBudget}
              isCost={false}
            />
            <SectionHeader label="Overhead" />
            {c.overheadRows.map(r => (
              <VarRow key={r.bucket} label={BUCKET_LABEL[r.bucket]}
                monthActual={r.month.actual} monthRev={c.monthRevenue} monthBudget={r.month.budgetAmt}
                ytdActual={r.ytd.actual}     ytdRev={c.ytdRevenue}     ytdBudget={r.ytd.budgetAmt}
                isCost
              />
            ))}
            <VarRow label="Total Overhead" subtotal
              monthActual={c.monthOverhead} monthRev={c.monthRevenue} monthBudget={c.monthOverheadBudget}
              ytdActual={c.ytdOverhead}     ytdRev={c.ytdRevenue}     ytdBudget={c.ytdOverheadBudget}
              isCost
            />
            {(c.monthNonOpex > 0 || c.ytdNonOpex > 0) && (
              <VarRow label="Non-OPEX"
                monthActual={c.monthNonOpex} monthRev={c.monthRevenue} monthBudget={0}
                ytdActual={c.ytdNonOpex}     ytdRev={c.ytdRevenue}     ytdBudget={0}
                isCost
              />
            )}
            <VarRow label="Net Profit" highlight
              monthActual={c.monthNet} monthRev={c.monthRevenue} monthBudget={c.monthNetBudget}
              ytdActual={c.ytdNet}     ytdRev={c.ytdRevenue}     ytdBudget={c.ytdNetBudget}
              isCost={false}
            />
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SectionHeader({ label }) {
  return (
    <tr>
      <td colSpan={11} style={S.tdSection}>{label}</td>
    </tr>
  );
}

function VarRow({ label, monthActual, monthRev, monthBudget, ytdActual, ytdRev, ytdBudget, isCost, highlight, subtotal }) {
  const monthActualPct = monthRev > 0 ? monthActual / monthRev : 0;
  const monthBudgetPct = monthRev > 0 ? monthBudget / monthRev : 0;
  const monthVar = monthBudget > 0 ? (monthActual - monthBudget) / monthBudget : (monthActual === 0 ? 0 : 1);
  const ytdActualPct = ytdRev > 0 ? ytdActual / ytdRev : 0;
  const ytdBudgetPct = ytdRev > 0 ? ytdBudget / ytdRev : 0;
  const ytdVar = ytdBudget > 0 ? (ytdActual - ytdBudget) / ytdBudget : (ytdActual === 0 ? 0 : 1);
  const rowStyle = highlight ? S.trHighlight : subtotal ? S.trSubtotal : null;
  return (
    <tr style={rowStyle}>
      <td style={{ ...S.tdLabel, fontWeight: highlight || subtotal ? 700 : 500 }}>{label}</td>
      <td style={S.td}>{fmtMoney(monthActual)}</td>
      <td style={S.tdPct}>{fmtPct(monthActualPct)}</td>
      <td style={S.tdMuted}>{fmtMoney(monthBudget)}</td>
      <td style={S.tdMutedPct}>{fmtPct(monthBudgetPct)}</td>
      <td style={{ ...S.tdVar, color: varianceColor(monthVar, isCost) }}>{fmtChange(monthVar)}</td>
      <td style={S.td}>{fmtMoney(ytdActual)}</td>
      <td style={S.tdPct}>{fmtPct(ytdActualPct)}</td>
      <td style={S.tdMuted}>{fmtMoney(ytdBudget)}</td>
      <td style={S.tdMutedPct}>{fmtPct(ytdBudgetPct)}</td>
      <td style={{ ...S.tdVar, color: varianceColor(ytdVar, isCost) }}>{fmtChange(ytdVar)}</td>
    </tr>
  );
}

// ── Balance Sheet card ───────────────────────────────────────────────────
function BalanceSheetCard({ computed, period }) {
  const c = computed;
  const rows = [
    { label: "Cash",                              curr: c.bs.cash,         prior: c.bsPrior.cash },
    { label: `AR (${c.arSource})`,                curr: c.arNow,           prior: c.bsPrior.ar },
    { label: "AP",                                curr: c.bs.ap,           prior: c.bsPrior.ap, isCost: true },
    { label: "Credit Cards",                      curr: c.bs.credit_cards, prior: c.bsPrior.credit_cards, isCost: true },
    { label: "LOC",                               curr: c.bs.loc,          prior: c.bsPrior.loc, isCost: true },
    { label: "Quick Ratio",                       curr: c.quickRatio,      prior: c.quickRatioPrior, isRatio: true },
  ];
  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <div style={S.cardTitle}>Balance Sheet — end of {periodLabel(period)}</div>
      </div>
      <div style={S.bsGrid}>
        <div style={{ flex: 2 }}>
          <table style={S.bsTable}>
            <thead>
              <tr>
                <th style={S.th}></th>
                <th style={S.th}>Current</th>
                <th style={S.th}>Prior</th>
                <th style={S.th}>Δ $</th>
                <th style={S.th}>Δ %</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const delta = (r.curr || 0) - (r.prior || 0);
                const deltaPct = r.prior ? delta / r.prior : 0;
                return (
                  <tr key={r.label}>
                    <td style={S.tdLabel}>{r.label}</td>
                    <td style={S.td}>{r.isRatio ? (r.curr?.toFixed(2) ?? "—") : fmtMoney(r.curr)}</td>
                    <td style={S.tdMuted}>{r.isRatio ? (r.prior?.toFixed(2) ?? "—") : fmtMoney(r.prior)}</td>
                    <td style={{ ...S.td, color: r.isCost ? (delta > 0 ? "#ef4444" : "#22c55e") : (delta > 0 ? "#22c55e" : "#ef4444") }}>
                      {r.isRatio ? (delta?.toFixed(2)) : fmtMoney(delta)}
                    </td>
                    <td style={S.tdPct}>{fmtChange(deltaPct)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={{ flex: 1 }}>
          <RatioBlock label="Quick Asset Ratio" value={c.quickRatio?.toFixed(2)} formula={`(Cash + ${c.arSource} AR) / (AP + CC + LOC)`} target="≥ 3.0" />
          <RatioBlock label="Debt to Equity"    value={c.debtToEquity?.toFixed(2)} formula="Total Debt / Total Equity" />
          <RatioBlock label="Return on Assets"  value={c.returnOnAssets != null ? fmtPct(c.returnOnAssets) : null} formula="Net Income (YTD) / Total Assets" />
          <RatioBlock label="AR Turnover"       value={c.arTurnover?.toFixed(2)} formula={`YTD Sales / Average ${c.arSource} AR`} />
          <RatioBlock label="Cash Runway"       value={c.cashRunwayMonths?.toFixed(1) + " mo"} formula={`(Cash + ${c.arSource} AR − AP − CC − LOC) ÷ Monthly Overhead`} />
        </div>
      </div>
    </div>
  );
}
function RatioBlock({ label, value, formula, target }) {
  return (
    <div style={S.ratioRow}>
      <div style={S.ratioLabel}>{label}</div>
      <div style={S.ratioValue}>{value || "—"}</div>
      <div style={S.ratioFormula}>{formula}{target ? ` · target ${target}` : ""}</div>
    </div>
  );
}

// ── Marketing card ────────────────────────────────────────────────────────
function MarketingCard({ snapshot }) {
  const vendors = snapshot.marketing_vendors || [];
  const total = vendors.reduce((s, v) => s + v.amount, 0);
  return (
    <div style={S.card}>
      <div style={S.cardHeader}>
        <div style={S.cardTitle}>Marketing Spend by Vendor — {periodLabel(snapshot.period)}</div>
        <div style={S.cardSubtitle}>{fmtMoney(total)} total · {vendors.length} vendor{vendors.length === 1 ? "" : "s"}</div>
      </div>
      {vendors.length === 0 ? (
        <div style={S.emptyText}>No marketing spend tagged for this period. Tag advertising / marketing accounts in Account Mapping to populate.</div>
      ) : (
        <table style={S.bsTable}>
          <thead>
            <tr>
              <th style={{ ...S.th, textAlign: "left" }}>Vendor</th>
              <th style={S.th}>Spend</th>
              <th style={S.th}>% of total</th>
            </tr>
          </thead>
          <tbody>
            {vendors.map(v => (
              <tr key={v.vendor}>
                <td style={S.tdLabel}>{v.vendor}</td>
                <td style={S.td}>{fmtMoney(v.amount)}</td>
                <td style={S.tdPct}>{total > 0 ? fmtPct(v.amount / total) : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Unmapped accounts card ───────────────────────────────────────────────
function UnmappedAccountsCard({ rows, onClickEdit }) {
  const auto = rows.filter(r => r.auto_assigned);
  if (!auto.length) return null;
  return (
    <div style={S.cardWarning}>
      <div style={S.cardHeader}>
        <div style={S.cardTitle}>{auto.length} account(s) auto-assigned — please review</div>
        <button onClick={onClickEdit} style={S.btn}>Review Mapping</button>
      </div>
      <div style={S.cardSubtitle}>
        {auto.slice(0, 6).map(r => `${r.qb_account_name} → ${BUCKET_LABEL[r.bucket]}`).join(" · ")}
        {auto.length > 6 ? ` · +${auto.length - 6} more` : ""}
      </div>
    </div>
  );
}

// ── Period quick pick ────────────────────────────────────────────────────
function PeriodQuickPick({ currentPeriod, onPick }) {
  const recent = useMemo(() => {
    // Last 6 months ending current calendar month
    const out = [];
    const today = new Date();
    let y = today.getUTCFullYear();
    let m = today.getUTCMonth() + 1;
    for (let i = 0; i < 6; i++) {
      out.push(`${y}-${String(m).padStart(2, "0")}`);
      m--;
      if (m < 1) { m = 12; y--; }
    }
    return out;
  }, []);
  return (
    <div style={{ display: "flex", gap: 6 }}>
      {recent.map(p => (
        <button key={p} onClick={() => onPick(p)} style={p === currentPeriod ? S.chipActive : S.chip}>
          {p.slice(5)}/{p.slice(2, 4)}
        </button>
      ))}
    </div>
  );
}

// ── KPI tile ──────────────────────────────────────────────────────────────
function Kpi({ label, value, sub, highlight }) {
  const border = highlight === "good" ? "rgba(34,197,94,0.4)"
                : highlight === "bad" ? "rgba(239,68,68,0.4)"
                : highlight === "ok"  ? "rgba(245,158,11,0.4)"
                : "rgba(255,255,255,0.08)";
  return (
    <div style={{ ...S.kpiCard, borderColor: border }}>
      <div style={S.kpiLabel}>{label}</div>
      <div style={S.kpiValue}>{value}</div>
      {sub && <div style={S.kpiSub}>{sub}</div>}
    </div>
  );
}

// ── Budget Editor modal ──────────────────────────────────────────────────
function BudgetEditor({ budget, onClose, onSave, onImportWeights, busy }) {
  const [draft, setDraft] = useState(() => ({
    ...budget,
    line_pcts: { ...budget.line_pcts },
    monthly_weights: { ...budget.monthly_weights },
    reclass: Array.isArray(budget.reclass) ? budget.reclass.map(x => ({ ...x })) : [],
  }));
  const totalWeight = Object.values(draft.monthly_weights).reduce((s, v) => s + Number(v), 0);
  const setReclass = (i, patch) => setDraft(d => { const n = [...(d.reclass || [])]; n[i] = { ...n[i], ...patch }; return { ...d, reclass: n }; });
  const addReclass = () => setDraft(d => ({ ...d, reclass: [...(d.reclass || []), { from: "", to: "admin_wages", amount: "", note: "" }] }));
  const rmReclass = (i) => setDraft(d => ({ ...d, reclass: (d.reclass || []).filter((_, j) => j !== i) }));
  const handleSave = () => {
    onSave({
      year: draft.year,
      annual_revenue_target: Number(draft.annual_revenue_target),
      line_pcts: Object.fromEntries(Object.entries(draft.line_pcts).map(([k, v]) => [k, Number(v)])),
      monthly_weights: Object.fromEntries(Object.entries(draft.monthly_weights).map(([k, v]) => [k, Number(v)])),
      net_profit_target_pct: Number(draft.net_profit_target_pct),
      reclass: (draft.reclass || []).filter(r => r.to && Number(r.amount)).map(r => ({ from: r.from || "", to: r.to, amount: Number(r.amount), note: r.note || "" })),
      notes: draft.notes || null,
    });
    onClose();
  };
  return (
    <div style={S.modalBackdrop} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <div style={S.cardTitle}>Edit Budget — Fiscal Year {draft.year}</div>
          <button onClick={onClose} style={S.iconBtn}>×</button>
        </div>
        <div style={S.modalBody}>
          <label style={S.label}>Annual Revenue Target</label>
          <input type="number" value={draft.annual_revenue_target}
            onChange={e => setDraft({ ...draft, annual_revenue_target: e.target.value })}
            style={S.input} />
          <label style={S.label}>Net Profit Target % (of revenue)</label>
          <input type="number" step="0.001" value={draft.net_profit_target_pct}
            onChange={e => setDraft({ ...draft, net_profit_target_pct: e.target.value })}
            style={S.input} />

          <div style={{ marginTop: 16, fontWeight: 700, color: "#e8eaf0" }}>Line item % of revenue</div>
          <div style={S.gridTwo}>
            {[...DIRECT_COST_BUCKETS, ...OVERHEAD_BUCKETS].map(b => (
              <div key={b}>
                <label style={S.label}>{BUCKET_LABEL[b]}</label>
                <input type="number" step="0.01" value={draft.line_pcts[b] ?? 0}
                  onChange={e => setDraft({ ...draft, line_pcts: { ...draft.line_pcts, [b]: e.target.value } })}
                  style={S.input} />
              </div>
            ))}
          </div>

          <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontWeight: 700, color: "#e8eaf0" }}>Manual reclass corrections (fiscal year)</div>
            <button onClick={addReclass} style={S.btn}>+ Add</button>
          </div>
          <div style={{ fontSize: 11, color: "#8892a4", marginBottom: 6 }}>
            Move dollars between buckets on the actuals without touching QuickBooks (e.g. Taylor's mis-bucketed admin).
            From → To is a net-zero reclass; leave <b>From</b> blank to add a missing cost. Applied to the Fiscal-Year view and the planner baseline.
          </div>
          {(draft.reclass || []).map((rc, i) => (
            <div key={i} style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center" }}>
              <select value={rc.from} onChange={e => setReclass(i, { from: e.target.value })} style={{ ...S.select, flex: 1 }}>
                <option value="">From: add new</option>
                {BUCKET_OPTIONS.map(b => <option key={b} value={b}>{BUCKET_LABEL[b]}</option>)}
              </select>
              <span style={{ color: "#8892a4" }}>→</span>
              <select value={rc.to} onChange={e => setReclass(i, { to: e.target.value })} style={{ ...S.select, flex: 1 }}>
                {BUCKET_OPTIONS.map(b => <option key={b} value={b}>{BUCKET_LABEL[b]}</option>)}
              </select>
              <input type="number" value={rc.amount} onChange={e => setReclass(i, { amount: e.target.value })} placeholder="$/yr" style={{ ...S.input, width: 110 }} />
              <input value={rc.note} onChange={e => setReclass(i, { note: e.target.value })} placeholder="note" style={{ ...S.input, flex: 1 }} />
              <button onClick={() => rmReclass(i)} style={S.iconBtn}>×</button>
            </div>
          ))}

          <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontWeight: 700, color: "#e8eaf0" }}>Monthly weights (sum: {(totalWeight * 100).toFixed(1)}%)</div>
            <button onClick={onImportWeights} disabled={busy} style={S.btn}>Import from prior fiscal year</button>
          </div>
          <div style={S.gridSix}>
            {Object.entries(draft.monthly_weights).map(([mm, w]) => (
              <div key={mm}>
                <label style={S.label}>{mm}</label>
                <input type="number" step="0.001" value={w}
                  onChange={e => setDraft({ ...draft, monthly_weights: { ...draft.monthly_weights, [mm]: e.target.value } })}
                  style={S.input} />
              </div>
            ))}
          </div>
        </div>
        <div style={S.modalFooter}>
          <button onClick={onClose} style={S.btn}>Cancel</button>
          <button onClick={handleSave} disabled={busy} style={S.btnPrimary}>{busy ? "Saving…" : "Save"}</button>
        </div>
      </div>
    </div>
  );
}

// ── Bucket Mapping editor ─────────────────────────────────────────────────
function BucketMappingEditor({ rows, onClose, onSave, busy }) {
  const [edits, setEdits] = useState({});   // name -> { bucket, pct, secondary }
  const [filter, setFilter] = useState("all");
  const visible = useMemo(() => {
    let r = rows;
    if (filter === "auto") r = rows.filter(x => x.auto_assigned);
    if (filter === "split") r = rows.filter(x => x.split && Object.keys(x.split).length);
    return [...r].sort((a, b) => a.qb_account_name.localeCompare(b.qb_account_name));
  }, [rows, filter]);

  // Initial split/primary state for a row (from its stored split, if any).
  const initialFor = (r) => {
    const split = r.split && Object.keys(r.split).length ? r.split : null;
    if (split) {
      const keys = Object.keys(split);
      const primaryKey = (r.bucket && split[r.bucket] != null) ? r.bucket : keys[0];
      return { bucket: primaryKey, pct: Math.round((split[primaryKey] || 0) * 100), secondary: keys.find(k => k !== primaryKey) || "" };
    }
    return { bucket: r.bucket, pct: 100, secondary: "" };
  };
  const rowState = (r) => edits[r.qb_account_name] || initialFor(r);
  const update = (r, patch) => setEdits(e => ({ ...e, [r.qb_account_name]: { ...rowState(r), ...patch } }));

  const handleSave = () => {
    const entries = Object.keys(edits).map(name => {
      const orig = rows.find(r => r.qb_account_name === name);
      const st = edits[name];
      let split = null;
      if (st.secondary && st.secondary !== st.bucket && st.pct > 0 && st.pct < 100) {
        split = { [st.bucket]: st.pct / 100, [st.secondary]: (100 - st.pct) / 100 };
      }
      return { qb_account_name: name, qb_account_type: orig?.qb_account_type, bucket: st.bucket, split, auto_assigned: false };
    });
    if (entries.length) onSave(entries);
    onClose();
  };
  const splitCount = rows.filter(r => r.split && Object.keys(r.split).length).length;

  return (
    <div style={S.modalBackdrop} onClick={onClose}>
      <div style={{ ...S.modal, maxWidth: 880 }} onClick={e => e.stopPropagation()}>
        <div style={S.modalHeader}>
          <div style={S.cardTitle}>QB Account → Bucket Mapping</div>
          <button onClick={onClose} style={S.iconBtn}>×</button>
        </div>
        <div style={{ ...S.modalBody, paddingTop: 0 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 8, position: "sticky", top: 0, background: "#161a20", paddingTop: 16, paddingBottom: 8, borderBottom: "1px solid rgba(255,255,255,0.06)", flexWrap: "wrap" }}>
            <button onClick={() => setFilter("all")} style={filter === "all" ? S.chipActive : S.chip}>All ({rows.length})</button>
            <button onClick={() => setFilter("auto")} style={filter === "auto" ? S.chipActive : S.chip}>Auto-assigned ({rows.filter(r => r.auto_assigned).length})</button>
            <button onClick={() => setFilter("split")} style={filter === "split" ? S.chipActive : S.chip}>Split ({splitCount})</button>
          </div>
          <div style={{ fontSize: 11, color: "#8892a4", marginBottom: 10 }}>
            To split one account across two buckets (e.g. Colton → 75% Direct Labour, 25% Admin Wages): set the primary bucket to <b>Labor</b>,
            <b> % to primary</b> = 75, and <b>secondary</b> = Admin Wages. The remainder goes to the secondary. Applied in ElecBid only — QuickBooks is never changed.
          </div>
          <table style={S.bsTable}>
            <thead>
              <tr>
                <th style={{ ...S.th, textAlign: "left" }}>QB Account</th>
                <th style={{ ...S.th, textAlign: "left" }}>Primary bucket</th>
                <th style={S.th}>% to primary</th>
                <th style={{ ...S.th, textAlign: "left" }}>Secondary (gets remainder)</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(r => {
                const st = rowState(r);
                const isSplit = st.secondary && st.secondary !== st.bucket && st.pct > 0 && st.pct < 100;
                return (
                  <tr key={r.qb_account_name} style={r.auto_assigned ? { background: "rgba(245,158,11,0.05)" } : null}>
                    <td style={S.tdLabel}>{r.qb_account_name}{r.auto_assigned ? " ⚠" : ""}{isSplit ? " ⇄" : ""}</td>
                    <td>
                      <select value={st.bucket} onChange={e => update(r, { bucket: e.target.value })} style={S.select}>
                        {ALL_BUCKETS.map(b => <option key={b} value={b}>{BUCKET_LABEL[b]}</option>)}
                      </select>
                    </td>
                    <td style={{ textAlign: "right" }}>
                      <input type="number" min="0" max="100" value={st.pct}
                             onChange={e => update(r, { pct: Number(e.target.value) })}
                             style={{ ...S.input, width: 64, textAlign: "right" }} />
                    </td>
                    <td>
                      <select value={st.secondary} onChange={e => update(r, { secondary: e.target.value })} style={S.select}>
                        <option value="">— none —</option>
                        {ALL_BUCKETS.map(b => <option key={b} value={b}>{BUCKET_LABEL[b]}</option>)}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={S.modalFooter}>
          <button onClick={onClose} style={S.btn}>Cancel</button>
          <button onClick={handleSave} disabled={busy || !Object.keys(edits).length} style={S.btnPrimary}>
            {busy ? "Saving…" : `Save ${Object.keys(edits).length} change(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────
const S = {
  page: { padding: 24, color: "#e8eaf0", fontFamily: "'Roboto', sans-serif", maxWidth: 1400, margin: "0 auto" },
  header: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 12 },
  title: { fontSize: 28, fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.02em" },
  subtitle: { fontSize: 13, color: "#8892a4", marginTop: 4 },
  headerActions: { display: "flex", gap: 8 },
  controlsBar: { display: "flex", gap: 10, alignItems: "center", marginBottom: 16, flexWrap: "wrap" },
  controlLabel: { fontSize: 13, color: "#8892a4" },
  select: { padding: "8px 10px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, color: "#f0f0f0", fontSize: 13, fontFamily: "inherit" },
  input: { width: "100%", padding: "8px 10px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, color: "#f0f0f0", fontSize: 13, fontFamily: "inherit", boxSizing: "border-box" },
  label: { fontSize: 12, color: "#8892a4", marginTop: 8, display: "block" },
  btn: { padding: "8px 14px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 6, color: "#f0f0f0", fontSize: 13, cursor: "pointer", fontFamily: "inherit" },
  btnPrimary: { padding: "8px 14px", background: "#a5ce3a", border: "none", borderRadius: 6, color: "#0d0f12", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  btnDanger: { padding: "8px 14px", background: "#ef4444", border: "none", borderRadius: 6, color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textDecoration: "none", display: "inline-block" },
  iconBtn: { background: "none", border: "none", color: "#f0f0f0", cursor: "pointer", fontSize: 22, lineHeight: 1, padding: 4 },
  linkBtn: { background: "none", border: "none", color: "#a5ce3a", cursor: "pointer", fontSize: "inherit", fontFamily: "inherit", marginLeft: 8 },
  errorBanner: { background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "#fca5a5", padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" },
  statusBanner: { background: "rgba(34,197,94,0.08)", border: "1px solid rgba(34,197,94,0.25)", color: "#86efac", padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center" },
  bannerClose: { background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: 18, lineHeight: 1, padding: 0, marginLeft: 12 },

  emptyCard: { background: "#161a20", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 32, textAlign: "center" },
  emptyTitle: { fontSize: 16, fontWeight: 700, marginBottom: 6 },
  emptyText: { fontSize: 13, color: "#8892a4" },

  card: { background: "#161a20", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 12, padding: 16, marginBottom: 16 },
  cardWarning: { background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 12, padding: 16, marginBottom: 16 },
  cardHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 8 },
  cardTitle: { fontSize: 14, fontWeight: 700, color: "#e8eaf0", letterSpacing: "0.01em" },
  cardSubtitle: { fontSize: 12, color: "#8892a4" },

  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 16 },
  kpiCard: { background: "#161a20", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: 14 },
  kpiLabel: { fontSize: 11, color: "#8892a4", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 },
  kpiValue: { fontSize: 22, fontWeight: 800, color: "#f0f0f0" },
  kpiSub:   { fontSize: 11, color: "#8892a4", marginTop: 4 },

  tableScroll: { overflowX: "auto" },
  varTable: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  bsTable:  { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { padding: "8px 10px", textAlign: "right", color: "#8892a4", fontWeight: 600, fontSize: 11, textTransform: "uppercase", borderBottom: "1px solid rgba(255,255,255,0.08)", whiteSpace: "nowrap" },
  thGroup: { padding: "6px 10px", textAlign: "center", color: "#e8eaf0", fontWeight: 700, fontSize: 11, textTransform: "uppercase", borderBottom: "1px solid rgba(255,255,255,0.12)" },
  thVar: { padding: "8px 10px", textAlign: "right", color: "#8892a4", fontWeight: 600, fontSize: 11, textTransform: "uppercase", borderBottom: "1px solid rgba(255,255,255,0.08)", borderLeft: "1px solid rgba(255,255,255,0.06)" },
  td: { padding: "8px 10px", textAlign: "right", color: "#f0f0f0", borderBottom: "1px solid rgba(255,255,255,0.04)", fontVariantNumeric: "tabular-nums" },
  tdMuted: { padding: "8px 10px", textAlign: "right", color: "#8892a4", borderBottom: "1px solid rgba(255,255,255,0.04)", fontVariantNumeric: "tabular-nums" },
  tdPct: { padding: "8px 10px", textAlign: "right", color: "#8892a4", borderBottom: "1px solid rgba(255,255,255,0.04)", fontSize: 11, fontVariantNumeric: "tabular-nums" },
  tdMutedPct: { padding: "8px 10px", textAlign: "right", color: "#5e6675", borderBottom: "1px solid rgba(255,255,255,0.04)", fontSize: 11, fontVariantNumeric: "tabular-nums" },
  tdLabel: { padding: "8px 10px", textAlign: "left", color: "#e8eaf0", borderBottom: "1px solid rgba(255,255,255,0.04)" },
  tdSection: { padding: "10px 10px 4px", color: "#a5ce3a", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" },
  tdVar: { padding: "8px 10px", textAlign: "right", borderBottom: "1px solid rgba(255,255,255,0.04)", borderLeft: "1px solid rgba(255,255,255,0.06)", fontWeight: 700, fontVariantNumeric: "tabular-nums" },
  trHighlight: { background: "rgba(165,206,58,0.04)" },
  trSubtotal:  { background: "rgba(255,255,255,0.02)", fontStyle: "italic" },

  bsGrid: { display: "flex", gap: 16, flexWrap: "wrap" },
  ratioRow: { padding: "10px 0", borderBottom: "1px solid rgba(255,255,255,0.04)" },
  ratioLabel: { fontSize: 11, color: "#8892a4", textTransform: "uppercase", letterSpacing: "0.04em" },
  ratioValue: { fontSize: 18, fontWeight: 700, color: "#f0f0f0" },
  ratioFormula: { fontSize: 10, color: "#5e6675", marginTop: 2 },

  metaFoot: { fontSize: 11, color: "#5e6675", padding: "8px 4px" },
  chip: { padding: "6px 10px", background: "#1e2328", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 6, color: "#8892a4", fontSize: 11, cursor: "pointer", fontFamily: "inherit" },
  chipActive: { padding: "6px 10px", background: "#a5ce3a", border: "1px solid #a5ce3a", borderRadius: 6, color: "#0d0f12", fontSize: 11, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },

  meetingsCard: {
    display: "flex", alignItems: "center", gap: 14,
    width: "100%", padding: "16px 18px", marginBottom: 16,
    background: "linear-gradient(135deg, rgba(165,206,58,0.08), rgba(165,206,58,0.02))",
    border: "1px solid rgba(165,206,58,0.25)", borderRadius: 12,
    color: "#e8eaf0", textAlign: "left", cursor: "pointer",
    fontFamily: "inherit", transition: "border-color 0.15s, transform 0.15s",
  },
  meetingsCardIcon: {
    width: 40, height: 40, borderRadius: 10,
    background: "rgba(165,206,58,0.12)", color: "#a5ce3a",
    display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
  },
  meetingsCardTitle: { fontSize: 15, fontWeight: 700, color: "#e8eaf0", marginBottom: 3 },
  meetingsCardDesc:  { fontSize: 12, color: "#8892a4" },
  meetingsCardArrow: { fontSize: 26, color: "#a5ce3a", fontWeight: 300, lineHeight: 1, paddingLeft: 8 },

  modalBackdrop: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 },
  modal: { background: "#161a20", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, width: "100%", maxWidth: 540, maxHeight: "90vh", display: "flex", flexDirection: "column" },
  modalHeader: { padding: "16px 20px", borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", justifyContent: "space-between", alignItems: "center" },
  modalBody: { padding: 20, overflowY: "auto", flex: 1 },
  modalFooter: { padding: 16, borderTop: "1px solid rgba(255,255,255,0.06)", display: "flex", justifyContent: "flex-end", gap: 8 },
  gridTwo: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
  gridSix: { display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: 6 },

  viewBar: { display: "flex", gap: 12, alignItems: "center", marginBottom: 12, flexWrap: "wrap" },
  segmented: { display: "inline-flex", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, overflow: "hidden" },
  seg: { padding: "8px 16px", background: "transparent", border: "none", color: "#8892a4", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" },
  segActive: { padding: "8px 16px", background: "#a5ce3a", border: "none", color: "#0d0f12", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" },
  rangeCaption: { fontSize: 13, color: "#a5ce3a", fontWeight: 600 },
  chartsGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(420px, 1fr))", gap: 16, marginBottom: 16 },
  chartTip: { background: "#161a20", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8, padding: "8px 10px", fontSize: 12, color: "#f0f0f0" },
};
